(ns common.schema.entities.workspace.kyc.questions.legal-entity
  (:require
   [common.email :as common-email]
   [common.country :as country]
   [common.schema.entities.workspace.kyc.kyc-options :as kyc-options]
   [common.schema.entities.workspace.kyc.questions.info-texts :as info-texts]
   [common.util :as cu]))

(defn template-has-question?
  [kyc-process question-attribute]
  (-> kyc-process
      :kyc-process/answers
      (contains? question-attribute)))

(def all-questions
  {:kyc-legal-entity/name
   {:data/type    :data.type/text-simple
    :translations {:en "Name of investor"
                   :fi "Sijoittajan nimi"}}

   :kyc-legal-entity/target-company-name
   {:data/type    :data.type/text-simple
    :translations {:en "Entity name"
                   :fi "Toiminimi"}}

   :kyc-legal-entity/tax-country
   {:data/type    :data.type/keyword
    :options      country/options
    :translations {:en "Tax country"
                   :fi "Verotuksellinen kotipaikka"}}

   :kyc-legal-entity/bank-name
   {:data/type    :data.type/text-simple
    :translations {:en "Bank Name"
                   :fi "Pankin nimi"}}

   :kyc-legal-entity/bank-address
   {:data/type    :data.type/text-simple
    :translations {:en "Bank address"
                   :fi "Pankin osoite"}}

   :kyc-legal-entity/bank-account-iban
   {:data/type    :data.type/text-simple
    :translations {:en "IBAN"
                   :fi "IBAN"}}

   :kyc-legal-entity/bank-account-swift
   {:data/type    :data.type/text-simple
    :translations {:en "SWIFT"
                   :fi "SWIFT"}}

   :kyc-legal-entity/incorporation-date
   {:data/type :data.type/date
    :translations {:en "Incorporation date"}}

   :kyc-legal-entity/country-of-incorporation
   {:data/type :data.type/keyword
    :options   country/options
    :translations {:en "Country of incorporation"}}

   :kyc-legal-entity/is-the-investment-made-on-your-own-behalf
   {:data/type :data.type/keyword
    :options   kyc-options/yes-no-options
    :translations {:en "Is the investment made on your own behalf?"}}

   :kyc-legal-entity/name-of-third-party
   {:data/type :data.type/text-simple
    :conditional-field (fn [answers]
                         (-> answers
                             :kyc-legal-entity/is-the-investment-made-on-your-own-behalf
                             (= :no)))
    :translations {:en "Name of third party"}}

   :kyc-legal-entity/country-of-third-party
   {:data/type :data.type/keyword
    :options country/options
    :conditional-field (fn [answers]
                         (-> answers
                             :kyc-legal-entity/is-the-investment-made-on-your-own-behalf
                             (= :no)))
    :translations {:en "Country of third party"}}

   :kyc-legal-entity/bank-account-owner
   {:data/type    :data.type/text-simple
    :translations {:en "Owner of the account"
                   :fi "Tilin omistaja"}}

   :kyc-legal-entity/is-the-investor-tax-resident-in-a-jurisdiction
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the Investor tax resident in a jurisdiction?"}}

   :kyc-legal-entity/is-the-ibo-resident-in-a-jurisdiction-for-tax-purposes
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the IBO resident in a jurisdiction for tax purposes?"}}

   :kyc-legal-entity/jurisdiction-of-tax-residence
   {:data/type :data.type/keyword
    :options country/options
    :translations {:en "Jurisdiction of tax residence"}}

   :kyc-legal-entity/ibo-jurisdiction-of-tax-residence
   {:data/type :data.type/keyword
    :options country/options
    :translations {:en "IBO is resident in the following jurisdiction for tax purposes"}}

   :kyc-legal-entity/tin-in-the-jurisdiction-of-tax-residence
   {:data/type :data.type/text-simple
    :translations {:en "TIN in the jurisdiction of tax residence"}}

   :kyc-legal-entity/bank-account-user-right-holder
   {:data/type    :data.type/text-simple
    :translations {:en "User right holder"
                   :fi "Käyttöoikeuden haltija"}}

   :kyc-legal-entity/bank-account-open-date
   {:data/type    :data.type/date
    :translations {:en "Date of opening the account"
                   :fi "Tilin avaamispäivä"}}

   :kyc-legal-entity/entity-name
   {:data/type    :data.type/text-simple
    :translations {:en "Entity name"
                   :fi "Yhteisön nimi"}}

   :kyc-legal-entity/jurisdiction
   {:data/type    :data.type/keyword
    :options      country/options
    :translations {:en "Jurisdiction / country of origin"
                   :fi "Kotipaikka / maa"}}

   :kyc-legal-entity/business-id
   {:data/type    :data.type/text-simple
    :translations {:en "Business ID"
                   :fi "Y-tunnus"}}

   :kyc-legal-entity/trade-register-extract
   {:data/type        :data.type/file
    :mandatory-field? (fn [answers _kyc-process]
                        (not= :FI (:kyc-legal-entity/jurisdiction answers)))
    :translations     {:en "Trade register extract"
                       :fi "Kaupparekisteriote (jollei se ole saatavilla julkisista lähteistä)"}}

   :kyc-legal-entity/trade-register-extract-mandatory
   {:data/type        :data.type/file
    :mandatory-field? (fn [_answers kyc-process]
                        (not= (:kyc-process/status kyc-process)
                              :kyc-process.status/draft))
    :translations     {:en "Trade register extract"
                       :fi "Kaupparekisteriote"}}

   :kyc-legal-entity/trade-register-extract-of-entity-mandatory
   {:data/type        :data.type/file
    :mandatory-field? (fn [_answers kyc-process]
                        (not= (:kyc-process/status kyc-process)
                              :kyc-process.status/draft))
    :translations     {:en "Up to date trade register extract of the Entity"
                       :fi "Yhteisön ajantasainen kaupparekisteriote"}}

   :kyc-legal-entity/signed-share-register
   {:data/type :data.type/file
    :translations {:en "Up to date signed share register"}}

   :kyc-legal-entity/latest-available-financial-statement
   {:data/type :data.type/file
    :translations {:en "Latest available financial statement for the legal entity"}}

   :kyc-legal-entity/recent-bank-statement
   {:data/type :data.type/file
    :translations {:en "A recent bank statement evidencing ownership of the bank account"}}

   :kyc-legal-entity/aml-letter
   {:data/type :data.type/file
    :translations {:en "AML letter (if available)"}}

   :kyc-legal-entity/confirm-funds-not-proceeds-of-crime
   {:data/type :data.type/confirm
    :editor/type :editor.type/checkbox
    :translations {:en "I/We confirm that the information provided above is a full and accurate disclosure of the source of funds/wealth and that the funds have no connection whatsoever with the financing of terrorism activities and  are not the proceeds of any crime or criminal activity. The Legal Entity is acting for the ultimate economic benefit or under the control of the individual(s) indicated in this form and it is not acting in the economic benefit or interest of any other undisclosed person(s) and/or organization(s). 
                                              
                                              I/We and/or the Legal Entity will not contravene with the provisions of Luxembourg laws and regulations on the prevention of money laundering and terrorism financing. 
                                              
                                              I/We further confirm that I/we and/or the Legal Entity will provide Lifeline Ventures with any information or documents that it may require in order to satisfy itself that the money or property is not the proceeds of any crime or criminal activity"}}

   ;; LE.8 - Ownership threshold question (determines UBO vs SMO flow)
   :kyc-legal-entity/natural-person-25-percent-ownership
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Does any natural person hold 25% or more of the shares or voting rights in the entity, or otherwise exercise control over the entity?"
                   :fi "Omistaako jokin luonnollinen henkilö 25% tai enemmän yhteisön osakkeista tai äänivallasta, tai muutoin käyttää määräysvaltaa yhteisössä?"}}

   :kyc-legal-entity/nature-and-purpose-of-business-relationship
   {:data/type :data.type/keyword
    :options  [{:key :investment-in-funds
                :ord 1
                :translations {:en "Investment in fund(s)"}}
               {:key :other
                :ord 2
                :translations {:en "Other"}}]
    :translations {:en "Nature and purpose of the business relationship"}}

   :kyc-legal-entity/nature-and-purpose-of-business-relationship-other
   {:data/type :data.type/text-simple
    :conditional-field (fn [answers]
                         (-> answers
                             :kyc-legal-entity/nature-and-purpose-of-business-relationship
                             (= :other)))
    :translations {:en "Specify nature and purpose of the business relationship"}}

   :kyc-legal-entity/legal-form
   {:data/type    :data.type/keyword
    :options      kyc-options/legal-form-options
    :translations {:en "Legal form"
                   :fi "Yhtiömuoto"}}

   :kyc-legal-entity/legal-form-other
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :legal-form/other (:kyc-legal-entity/legal-form answers)))
    :translations      {:en "Other legal form, specify"
                        :fi "Muu yhtiömuoto, selite"}}

   :kyc-legal-entity/innovestor-line-of-business
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations
    {:en
     "Does your company operate in one of the following sectors: construction, pharmaceuticals, healthcare, arms dealing, defence, mining, public procurement, financial services, casino operations or precious metals brokerage?"
     :fi
     "Toimiiko yrityksenne jollain seuraavista toimialoista: rakennusala, lääkeala, terveydenhuoltoala, asekauppa, puolustusteollisuus, kaivannaisteollisuus, julkinen hankinta, rahapalvelut, kasinotoiminta tai jalometallien välittäminen?"}}

   :kyc-legal-entity/line-of-business-detailed-description
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/innovestor-line-of-business answers)))
    :translations      {:en "Detailed description of the line of business"
                        :fi "Tarkempi toimialan kuvaus"}}

   :kyc-legal-entity/actual-line-of-business
   {:data/type    :data.type/keyword-multi
    :options      kyc-options/industry-options
    :translations {:en "Actual line of business"
                   :fi "Toimiala"}}

   :kyc-legal-entity/actual-line-of-business-other
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (some #{:industry/other}
                               (:kyc-legal-entity/actual-line-of-business answers)))
    :translations      {:en "Other actual line of business, specify"
                        :fi "Muu toimiala, selite"}}

   :kyc-legal-entity/articles-of-association-or-incorporation
   {:data/type    :data.type/file
    :translations {:en "Articles of association / incorporation"}}

   :kyc-legal-entity/line-of-business-text
   {:data/type    :data.type/text-simple
    :translations {:en "Line of business"
                   :fi "Toimiala"}}

   :kyc-legal-entity/address
   {:data/type    :data.type/text-multiline
    :translations {:en "Official street address"
                   :fi "Osoite"}}

   :kyc-legal-entity/state
   {:data/type :data.type/text-simple
    :translations {:en "State"}}

   :kyc-legal-entity/zip-code
   {:data/type    :data.type/text-simple
    :translations {:en "Zip code"
                   :fi "Postinumero"}}

   :kyc-legal-entity/city
   {:data/type    :data.type/text-simple
    :translations {:en "City"
                   :fi "Kaupunki"}}

   :kyc-legal-entity/country
   {:data/type    :data.type/keyword
    :options      country/options
    :translations {:en "Country"
                   :fi "Maa"}}

   :kyc-legal-entity/main-business-address
   {:data/type       :data.type/text-multiline
    :optional-field? true
    :translations    {:en "Main business address (if different from main address)"
                      :fi
                      "Pääasiallisen liiketoimintapaikan osoite (jos eroaa kotipaikan osoitteesta)"}}

   ;; Mailing address fields (optional)
   :kyc-legal-entity/mailing-address
   {:data/type       :data.type/text-multiline
    :optional-field? true
    :translations    {:en "Mailing address (if different from official address)"
                      :fi "Postiosoite (jos eroaa virallisesta osoitteesta)"}}

   :kyc-legal-entity/mailing-state
   {:data/type       :data.type/text-simple
    :optional-field? true
    :translations    {:en "Mailing state"
                      :fi "Postiosoitteen osavaltio"}}

   :kyc-legal-entity/mailing-zip-code
   {:data/type       :data.type/text-simple
    :optional-field? true
    :translations    {:en "Mailing zip code"
                      :fi "Postiosoitteen postinumero"}}

   :kyc-legal-entity/mailing-city
   {:data/type       :data.type/text-simple
    :optional-field? true
    :translations    {:en "Mailing city"
                      :fi "Postiosoitteen kaupunki"}}

   :kyc-legal-entity/mailing-country
   {:data/type       :data.type/keyword
    :options         country/options
    :optional-field? true
    :translations    {:en "Mailing country"
                      :fi "Postiosoitteen maa"}}

   :kyc-legal-entity/financial-records-delivery
   {:data/type    :data.type/keyword
    :translations {:en "Financial records delivery"
                   :fi "Tilinpäätös / vuosikertomus"}
    :options      kyc-options/attachment-or-url-options}

   :kyc-legal-entity/financial-records-url
   {:data/type         :data.type/text-url
    :conditional-field (fn [answers]
                         (= (:kyc-legal-entity/financial-records-delivery answers) :url))
    :translations      {:en "Financial records www-url"
                        :fi "Tilinpäätöksen / vuosikertomuksen www-osoite"}}

   :kyc-legal-entity/attachment-financial-records
   {:data/type         :data.type/file
    :conditional-field (fn [answers]
                         (= :attachment (:kyc-legal-entity/financial-records-delivery answers)))
    :translations      {:en "The most recent financial accounts and/or the annual report"
                        :fi "Tilinpäätös ja/tai vuosikertomus"}}


   :kyc-legal-entity/size-of-investment-portfolio
   {:data/type    :data.type/currency-value
    :translations {:en "Size of investor's investment portfolio (EUR)"
                   :fi "Sijoitusvarallisuuden arvo (EUR)"}
    :info         {:en "Remember to enter value in EURO's"
                   :fi "Muista syöttää valuutta euromääräisenä"}}

   :kyc-legal-entity/split-of-investment-portfolio
   {:data/type     :data.type/text-edn
    :edn-data/type :edn-data.type/split-of-investment-portfolio
    :options       kyc-options/split-of-investments-options
    :translations  {:en "Split of investment portfolio"
                    :fi "Sijoitusvarallisuuden arvon jakauma"}
    :info          {:en "Other investments : Real estate, alternative or unlisted investments"
                    :fi "Muut sijoitukset : Kiinteistöt, listaamattomat tai vaihtoehtoiset sijoitukset"}}

   :kyc-legal-entity/source-of-funds
   {:data/type    :data.type/keyword-multi
    :options      kyc-options/source-of-funds-options
    :translations {:en "Source of funds"
                   :fi "Sijoitusvarojen alkuperä"}}

   :kyc-legal-entity/source-of-funds-allocation-split
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/source-of-funds-allocation-split
    :options           kyc-options/source-of-funds-options
    :conditional-field (fn [answers]
                         (not-empty (set (:kyc-legal-entity/source-of-funds answers))))
    :translations      {:en "Source of funds (by %)"
                        :fi "Sijoitusvarojen alkuperän jakauma"}}

   :kyc-legal-entity/source-of-funds-comment
   {:data/type         :data.type/text-multiline
    :conditional-field (fn [answers]
                         (some (set (:kyc-legal-entity/source-of-funds answers))
                               [:source-of-funds/other ":source-of-funds/other"
                                "source-of-funds/other" "other"]))
    :translations      {:en "Source of funds - specify other source"
                        :fi "Sijoitusvarojen alkuperä - lisää selite"}}
   :kyc-legal-entity/source-of-wealth
   {:data/type    :data.type/text-multiline
    :translations {:en "Source of wealth"
                   :fi "Varallisuuden alkuperä"}}

   :kyc-legal-entity/listed-in-stock-exchange?
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the investor listed on stock exchange stated in details"
                   :fi "Onko sijoittaja listattu infossa tarkoitetun mukaiseen pörssiin?"}}

   :kyc-legal-entity/organization-chart-delivery
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers)))
    :options           (concat kyc-options/attachment-or-url-options
                               [{:key          :other
                                 :translations {:en "Other"
                                                :fi "Muu"}
                                 :ord          (float (inc
                                                       (count
                                                        kyc-options/attachment-or-url-options)))}])
    :translations      {:en "Ownership structure"
                        :fi "Organisaatiokaavio/rakenne"}}

   :kyc-legal-entity/attachment-organization-chart
   {:data/type         :data.type/file
    :conditional-field (fn [answers]
                         (= :attachment (:kyc-legal-entity/organization-chart-delivery answers)))
    :translations      {:en "The ownership structure chart"
                        :fi "Organisaatiokaavio/rakenne"}}

   :kyc-legal-entity/ownership-chart-url
   {:data/type         :data.type/text-url
    :conditional-field (fn [answers]
                         (and (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers))
                              (= :url (:kyc-legal-entity/organization-chart-delivery answers))))
    :translations      {:en "Ownership structure available on website"
                        :fi "Organisaationkaavio/rakenne saatavissa web-osoitteessa"}}

   :kyc-legal-entity/ownership-other
   {:data/type         :data.type/text-multiline
    :conditional-field (fn [answers]
                         (and (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers))
                              (= :other (:kyc-legal-entity/organization-chart-delivery answers))))
    :translations      {:en "Describe ownership structure"
                        :fi "Kuvaa organisaatiokaavio/rakenne tähän"}}

   :kyc-legal-entity/has-agreement-of-voting-rights?
   {:data/type         :data.type/keyword
    :options           kyc-options/yes-no-options
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers)))
    :translations
    {:en (str "Does the entity have a shareholder agreement or another agreement in which "
              "the entity’s ownership or voting rights are agreed?")
     :fi (str "Onko yhteisöllä osakas- tai muuta sopimusta, jossa on sovittu yhteisön "
              "omistus- tai äänivallasta?")}}

   :kyc-legal-entity/shareholder-or-other-agreement-description
   {:data/type         :data.type/text-multiline
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/has-agreement-of-voting-rights? answers)))
    :translations      {:en "Description of voting rights"
                        :fi "Kuvaus äänivallan jakautumisesta"}}

   :kyc-legal-entity/board-members-edn
   {:data/type     :data.type/text-edn
    :edn-data/type :edn-data.type/board-members-list
    :data/template nil ;; MUST BE OVERRIDDEN by schema to avoid circular references
    :translations  {:en "Board members"
                    :fi "Hallituksen jäsenet"}}

   :kyc-legal-entity/ubo-edn
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/ubo-list
    :conditional-field (fn [answers]
                         (not= :yes (:kyc-legal-entity/listed-in-stock-exchange? answers)))
    :mandatory-field? (fn [_answers kyc-process]
                        (and (not= (:kyc-process/status kyc-process)
                                   :kyc-process.status/draft)
                             (not (some #{(:kyc-process/template kyc-process)}
                                        [:kyc-template/innovestor-cofund-angel-entity
                                         :kyc-template/innovestor-cofund-angel-target-company]))))
    :data/template     nil ;; MUST BE OVERRIDDEN by schema to avoid circular references
    :translations      {:en "Ultimate beneficial owners (UBO)"
                        :fi "Yhteisön tosiasialliset edunsaajat"}}

   :kyc-legal-entity/smo-edn
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/smo-list
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/natural-person-25-percent-ownership answers)))
    :mandatory-field? (fn [_answers kyc-process]
                        (not= (:kyc-process/status kyc-process)
                              :kyc-process.status/draft))
    :data/template     nil ;; MUST BE OVERRIDDEN by schema to avoid circular references
    :translations      {:en "Senior Managing Officers (SMO)"
                        :fi "Johtavat toimihenkilöt"}}

   :kyc-legal-entity/contact-matrix-file
   {:data/type     :data.type/file
    :translations {:en "Contact matrix"}}

   :kyc-legal-entity/has-peps?
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Does investor have politically exposed person(s)?"
                   :fi "Liittyykö sijoittajaan poliittisesti vaikutusvaltaisia henkilöitä?"}}

   :kyc-legal-entity/entity-has-peps?
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Representative of the Entity is a politically exposed person (”PEP”)?"
                   :fi "Onko yhteisön edustaja poliittisesti vaikutusvaltainen henkilö?"}}

   :kyc-legal-entity/pep-edn
   {:conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/has-peps? answers)))
    :data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/pep-list
    :data/template     nil ;; MUST override
    :translations      {:en "PEP details"
                        :fi "PEP tiedot"}}

   :kyc-legal-entity/professional-investor?
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Professional investor?"
                   :fi "Ammattimainen sijoittaja"}}

   :kyc-legal-entity/professional-investor-criteria
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/professional-investor? answers)))
    :translations      {:en "Investor category"
                        :fi "Sijoittajatyyppi"}
    :info              info-texts/professional-investor-criteria-info
    :options           kyc-options/professional-investor-options}

   :kyc-legal-entity/large-undertaking-criteria
   {:data/type         :data.type/keyword-multi
    :conditional-field (fn [answers]
                         (= :professional-investor.type/large-undertaking
                            (:kyc-legal-entity/professional-investor-criteria answers)))
    :translations      {:en "Investor category"
                        :fi "Sijoittajatyyppi"}
    :options           kyc-options/large-undertaking-options}

   :kyc-legal-entity/other-professional-investor-type
   {:data/type         :data.type/keyword-multi
    :conditional-field (fn [answers]
                         (and (= :yes (:kyc-legal-entity/professional-investor? answers))
                              (= :professional-investor.type/other
                                 (:kyc-legal-entity/professional-investor-criteria answers))))
    :translations      {:en "Other investor criteria - select two"
                        :fi "Muu ammattimainen sijoittaja - valitse kaksi"}
    :info              {:en "Select at least two criteria"
                        :fi "Valitse ainakin kaksi vaihtoehtoa"}
    :options           kyc-options/other-investor-type-options}

   :kyc-legal-entity/primary-contact-email
   {:data/type :data.type/text-simple
    :translations {:en "Primary contact email"}}

   :kyc-legal-entity/professional-investor-notes
   {:data/type       :data.type/text-multiline
    :optional-field? true
    :translations    {:en "Notes"
                      :fi "Lisätietoja"}}

   :kyc-legal-entity/tax-countries
   {:data/type     :data.type/text-edn
    :edn-data/type :edn-data.type/tax-recidencies
    :translations  {:en "Tax residencies"
                    :fi "Verotusmaat"}
    :info          info-texts/tax-info}

   :kyc-legal-entity/usa-tax-residency
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "USA tax residency"
                   :fi "Yhdysvaltain verovelvollisuus"}
    :info         info-texts/tax-info}

   :kyc-legal-entity/usa-tax-residency-yes
   {:data/type         :data.type/confirm
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/usa-tax-residency answers)))
    :translations      {:en "I hereby confirm that the investor is not a tax resident in the USA."
                        :fi "Vahvistan, että sijoittaja ei ole verovelvollinen Yhdysvalloissa."}}

   :kyc-legal-entity/usa-tax-residency-no
   {:data/type         :data.type/confirm
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/usa-tax-residency answers)))
    :translations
    {:en (str "I hereby confirm that the investor is a tax resident in the USA, and that "
              "it has reported this tax residency and its Tax Identification Number (TIN) "
              "above.")
     :fi (str "Vahvistan, että sijoittaja on verovelvollinen Yhdysvalloissa, "
              "ja että verovelvollisuus sekä veronumero on ilmoitettu yllä.")}}

   :kyc-legal-entity/us-person-status
   {:data/type :data.type/keyword
    :editor/type :editor.type/radio
    :translations {:en "Please tick and complete as appropriate"}
    :options      [{:key :specified-us-person
                    :ord 1
                    :translations {:en "The entity is a Specified U.S. Person"}}
                   {:key :us-person
                    :ord 2
                    :translations {:en "The entity is a U.S. Person that is not a Specified U.S. Person."}}
                   {:key :not-us-person
                    :ord 3
                    :translations {:en "The entity is not a U.S. Person"}}]}

   :kyc-legal-entity/fatca-specified-us-person?
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "FATCA Declaration - is investor a Specified U.S. Person?"
                   :fi "FATCA luokittelu - onko sijoittaja Specified U.S. Person?"}
    :info         info-texts/fatca-declaration-info}

   :kyc-legal-entity/financial-institution?
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the investor a Financial Institution?"
                   :fi "Onko sijoittaja finanssilaitos?"}
    :info         info-texts/financial-institution-info}

   :kyc-legal-entity/is-registered-foreign-financial-institution
   {:data/type :data.type/keyword
    :options kyc-options/yes-no-options
    :translations {:en "Is the investor a Registered Foreign Financial Institution?"}}

   :kyc-legal-entity/fatca-classification
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/financial-institution? answers)))
    :options           kyc-options/fatca-categories
    :translations      {:en "FATCA Classification for Financial Institutions (FI)"
                        :fi "FATCA-luokitus, finanssilaitos"}
    :info              info-texts/tax-info}

   :kyc-legal-entity/fatca-classification-other
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :fatca.category/other (:kyc-legal-entity/fatca-classification answers)))
    :translations      {:en "Specify FATCA category"
                        :fi "Muu Fatca-luokitus"}}

   :kyc-legal-entity/have-GIIN?
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (and (not (some #{(:kyc-legal-entity/fatca-classification answers)}
                                         [:fatca.category/exempt-beneficial-owner
                                          :fatca.category/deemed-compliant
                                          :fatca.category/certified-deemed-compliant
                                          :fatca.category/non-participating-fi
                                          :fatca.category/excepted-ffi
                                          :fatca.category/us-person]))
                              (= :yes (:kyc-legal-entity/financial-institution? answers))))
    :options           kyc-options/yes-no-options
    :translations      {:en "Does investor have GIIN number?"
                        :fi "Onko sijoittajalla GIIN numero?"}
    :info              info-texts/fatca-declaration-info}

   :kyc-legal-entity/exempt-beneficial-owner-status
   {:data/type :data.type/keyword
    :options kyc-options/exempt-beneficial-owner-status-options
    :translations {:en "Indicate status for Exempt Beneficial Owner"}}

   :kyc-legal-entity/fatca-GIIN
   {:data/type    :data.type/text-simple
    :conditional-field (fn [answers]
                         (and (= :yes (:kyc-legal-entity/have-GIIN? answers))
                              (= :yes (:kyc-legal-entity/financial-institution? answers))))
    :translations {:en "Global Intermediary Identification Number (GIIN)"
                   :fi "GIIN numero"}
    :info         info-texts/tax-info}

   :kyc-legal-entity/fatca-no-GIIN
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (and (= :yes (:kyc-legal-entity/financial-institution? answers))
                              (= :no (:kyc-legal-entity/have-GIIN? answers))
                              (< (count (cu/str-trim (:kyc-legal-entity/fatca-GIIN answers))) 1)))
    :options           kyc-options/no-giin-reasons
    :translations      {:en "Reason for not having GIIN"
                        :fi "Syy miksi GIIN numeroa ei voida ilmoittaa"}
    :info              info-texts/tax-info}

   :kyc-legal-entity/fatca-no-GIIN-other
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :no-giin-reason/other (:kyc-legal-entity/fatca-no-GIIN answers)))
    :translations      {:en "Other reason for not having GIIN"
                        :fi "Muu syy GIIN puuttumiseen"}}

   :kyc-legal-entity/GIIN-Sponsor
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :no-giin-reason/sponsor (:kyc-legal-entity/fatca-no-GIIN answers)))
    :translations      {:en "GIIN Sponsor name"
                        :fi "GIIN Sponsorin nimi"}}

   :kyc-legal-entity/GIIN-Sponsor-GIIN
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :no-giin-reason/sponsor (:kyc-legal-entity/fatca-no-GIIN answers)))
    :translations      {:en "GIIN Sponsor's GIIN number"
                        :fi "GIIN Sponsorin GIIN numero"}}

   :kyc-legal-entity/non-financial-entity-classification
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/financial-institution? answers)))
    :options           kyc-options/non-financial-classifications
    :translations      {:en "FATCA Classification for Non-Financial Entity"
                        :fi "FATCA-luokitus, ei-finanssiyksikkö"}
    :info              info-texts/tax-info}

   :kyc-legal-entity/non-financial-entity-classification-other
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :non-fatca.category/other
                            (:kyc-legal-entity/non-financial-entity-classification answers)))
    :translations      {:en "Specify non-financial FATCA category"
                        :fi "Muu ei-finanssiyksikön Fatca-luokitus"}}

   :kyc-legal-entity/crs-classification
   {:data/type    :data.type/keyword
    :options      kyc-options/crs-classifications
    :translations {:en "CRS classification"
                   :fi "CRS luokitus"}}

   :kyc-legal-entity/crs-classification-other
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :crs-institution/other (:kyc-legal-entity/crs-classification answers)))
    :translations      {:en "Other CRS classification, please specify"
                        :fi "Muu CRS luokitus, lisää selite"}}

   :kyc-legal-entity/crs-tax-reporting-info
   {:data/type       :data.type/text-multiline
    :optional-field? true
    :translations    {:en "Additional information related to tax reporting"
                      :fi "Lisätietoja veroraportointiin liittyen"}}

   :kyc-legal-entity/sactions-target
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations
    {:en (str "To the best of your knowledge, are you/the investor, or a connected party "
              "(Beneficial Owners, Key Controllers, Trustees, Directors, Business Partners) "
              "to you/ the  investor targeted by any sanctions administered by a "
              "Sanctions Authority and/or any other applicable local sanctions law, or have "
              "you or any of the aforementioned parties previously been targeted?")
     :fi (str
          "Parhaan tietämyksesi mukaan, oletko sinä/sijoittaja tai joku lähipiiriin kuuluva "
          "(tosiasialliset edunsaajat, päättävässä asemassa olevat, yhtiökumppanit) nyt "
          "tai aikaisemmin ollut jonkun viranomaisen pakotteiden tai pakotesääntelyn kohteena?")}}

   :kyc-legal-entity/capman-sanctions-confirmation
   {:data/type :data.type/keyword
    :options   [{:ord 1.0
                 :key :yes-i-confirm
                 :translations {:en "Yes, I confirm"
                                :fi "Kyllä, vahvistan"}}
                {:ord 2.0
                 :key :no-i-cannot-confirm
                 :translations {:en "No, I cannot confirm"
                                :fi "Ei, en pysty vahvistamaan"}}]
    :translations {:en "Please confirm if you comply with all applicable national and international
sanctions regulations (including sanctions imposed by Finland, the EU, UN, UK
(HMT), and the US (OFAC)) and that, to the best of your knowledge, you or any
person or entity directly or indirectly related to you (including beneficial owners,
directors, or authorised signatories) are not subject to such sanctions, asset
freezes, or trade restrictions."
                   :fi "Vahvistakaa noudatatteko kaikkia sovellettavia kansallisia ja kansainvälisiä
pakotelakeja sekä -sääntelyä (mukaan lukien Suomen, EU:n, YK:n, IsonBritannian (HMT) ja Yhdysvaltojen (OFAC) asettamat pakotteet) ja että parhaan
tietonne mukaan te tai kukaan teihin suoraan tai välillisesti liittyvä taho (mukaan
lukien tosiasialliset edunsaajat, hallituksen jäsenet tai valtuutetut
allekirjoittajat) ei ole tällaisten pakotteiden, varojen jäädyttämisen tai
kaupankäynninrajoitusten kohteena."}}

   :kyc-legal-entity/capman-sanctions-cannot-confirm-clarification
   {:data/type :data.type/text-multiline
    :translations {:fi "Jos ette pysty vahvistamaan, kertokaa tälle syy"
                   :en "If you cannot confirm, please provide details"}}

   :kyc-legal-entity/geographical-exposure
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Geographical and International Sanction Exposure"
                   :fi "Pakotteiden alainen maantieteellinen ja kansainvälinen toiminta"}
    :info
    {:en
     (str
      "<p>Does the investor and/or a connected party have any current or planned "
      "presence (1) and/or activity (2) directly or indirectly involving any of the "
      "countries or territories impacted by YK/EU/US sanctions?</p>"
      "<p><strong>Where to check:</strong>"
      "<ul><li><a href=\"https://www.un.org/securitycouncil/content/un-sc-consolidated-list\">"
      "https://www.un.org/securitycouncil/content/un-sc-consolidated-list</a></li>"
      "<li><a href=\"https://sanctionsmap.eu/#/main\">https://sanctionsmap.eu/#/main</a></li>"
      "<li><a href=\"https://home.treasury.gov/policy-issues/financial-sanctions/sanctions-programs-and-country-information\">"
      "https://home.treasury.gov/policy-issues/financial-sanctions/sanctions-programs-and-country-information</a</li>"
      "</ul></p>")
     :fi
     (str
      "<p>Onko sijoittajalla tai sen lähipiiriin kuuluvalla nykyistä tai suunniteltua, "
      "suoraa tai epäsuoraa läsnäoloa (1) ja/tai toimintaa (2) missään maissa tai alueilla, "
      "jotka ovat YK:n/EU:n/USA:n pakotteiden kohteina?</p>"
      "<p><strong>Listat maista:</strong>"
      "<ul><li><a href=\"https://www.un.org/securitycouncil/content/un-sc-consolidated-list\">"
      "https://www.un.org/securitycouncil/content/un-sc-consolidated-list</a></li>"
      "<li><a href=\"https://sanctionsmap.eu/#/main\">https://sanctionsmap.eu/#/main</a></li>"
      "<li><a href=\"https://home.treasury.gov/policy-issues/financial-sanctions/sanctions-programs-and-country-information\">"
      "https://home.treasury.gov/policy-issues/financial-sanctions/sanctions-programs-and-country-information</a</li>"
      "</ul></p>")}}

   :kyc-legal-entity/number-of-documents
   {:data/type    :data.type/keyword
    :options      [{:key          :none
                    :translations {:en "None"
                                   :fi "0"}
                    :ord          0.0}
                   {:key          :one
                    :translations {:en "1"
                                   :fi "1"}
                    :ord          1.0}
                   {:key          :two
                    :translations {:en "2"
                                   :fi "2"}
                    :ord          2.0}
                   {:key          :three
                    :translations {:en "3"
                                   :fi "3"}
                    :ord          3.0}
                   {:key          :four
                    :translations {:en "4"
                                   :fi "4"}
                    :ord          4.0}
                   {:key          :five
                    :translations {:en "5"
                                   :fi "5"}
                    :ord          5.0}]
    :translations {:en "Number of additional documents"
                   :fi "Lisättävien dokumenttien lukumäärä"}}
   :kyc-legal-entity/document-1
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-documents answers)}
                               [:one :two :three :four :five]))
    :translations      {:en "Document 1"
                        :fi "Dokumentti 1"}}
   :kyc-legal-entity/document-2
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-documents answers)}
                               [:two :three :four :five]))
    :translations      {:en "Document 2"
                        :fi "Dokumentti 2"}}
   :kyc-legal-entity/document-3
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-documents answers)}
                               [:three :four :five]))
    :translations      {:en "Document 3"
                        :fi "Dokumentti 3"}}
   :kyc-legal-entity/document-4
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-documents answers)} [:four :five]))
    :translations      {:en "Document 4"
                        :fi "Dokumentti 4"}}
   :kyc-legal-entity/document-5
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-documents answers)} [:five]))
    :translations      {:en "Document 5"
                        :fi "Dokumentti 5"}}

   :kyc-legal-entity/tax-status-in-home-jurisdiction
   {:data/type :data.type/keyword
    :options      [{:ord 1.0
                    :key :tax-status/tax-payer
                    :translations {:en "Tax payer"}}
                   {:ord 2.0
                    :key :tax-status/tax-exempt
                    :translations {:en "Tax exempt"}}
                   {:ord 3.0
                    :key :tax-status/flow-through
                    :translations {:en "Flow-through"}}]
    :translations {:en "Investor's tax status in home jurisdiction"}
    :info {:en (str "Tax Status of Investor in Investor's jurisdiction of tax residence "
                    "(or, if Investor is not a resident for tax purposes in any jurisdiction, "
                    "in the Investor's jurisdiction of organisation, establishment or citizenship) "
                    "(such jurisdiction, the Investor's \"Home Jurisdiction\").")}
    :info-position :below-input}

   :kyc-legal-entity/flow-through-ownership-jurisdictions
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/flow-through-ownership-jurisdictions
    :data/template     nil ;; MUST BE OVERRIDDEN by schema to avoid circular references
    :translations      {:en "Flow-through ownership jurisdictions"}
    :info              {:en (str "Please complete this table in respect of the ultimate investors in the Investor/IBO (i.e. the owners of the Investor/IBO). In the case of investors who are themselves partnerships/transparent/flow-through entities in their home jurisdiction, please " "\"go up\"" " the ownership chain and provide the details of the first owner in the relevant chain that is not a partnership/flow-through entity. If you are unable to answer a question, or an answer is unclear as a matter of law, then please add this to the notes for the jurisdiction. Please complete columns \"Jurisdiction\" and \"Ownership %\" even if you do not know the answers for the rest of the columns." "")}}

   :kyc-legal-entity/beneficial-ownership
   {:data/type :data.type/keyword
    :translations {:en "Beneficial ownership"}
    :options   [{:key :as-itself
                 :ord 1
                 :translations {:en "The Investor will hold its interest in the Partnership for itself beneficially"}}
                {:key :as-trustee
                 :ord 1
                 :translations {:en "The Investor will hold its interest in the Partnership as trustee/nominee for the following beneficiary"}}]}

   :kyc-legal-entity/ibo-name
   {:data/type :data.type/text-simple
    :translations {:en "Investor's Beneficial Owner's (\"IBO\") name"}}

   :kyc-legal-entity/please-confirm-ibo-statements-info
   {:data/type       :data.type/info
    :optional-field? true
    :translations
    {:en "Please confirm whether the IBO considers each of the following three statements to be True or False (or, where relevant, N/A):"}}

   :kyc-legal-entity/depending-on-ibo-answers-above-we-may-require-further-information-from-ibo
   {:data/type       :data.type/info
    :optional-field? true
    :translations
    {:en "PLEASE NOTE: Depending on the IBO’s answers above, we may require further information from the IBO."}}

   :kyc-legal-entity/lifeline-gp-or-aifm-maybe-require-more-information-info
   {:data/type       :data.type/info
    :optional-field? true
    :translations
    {:en "PLEASE NOTE: Depending on your answers above, the General Partner or the AIFM may require further information from you."}}

   :kyc-legal-entity/scsp-transparent-under-investors-laws
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-na-options
    :translations {:en "Under the tax laws of the Investor's Home Jurisdiction, the Partnership set up in the form of a Luxembourg special limited partnership (SCSp, or société en commandite spéciale) is regarded as an entity which is transparent, \"flow-through\" or disregarded"}
    :info {:en "For purposes of this question, \"disregarded\" applies where the Partnership’s income or expenses are treated as income or expenses of the Investor under the laws of the Investor’s Home Jurisdiction."}
    :info-position :below-input}

   :kyc-legal-entity/scsps-transparent-under-ibos-laws
   {:data/type :data.type/keyword
    :options kyc-options/true-false-na-options
    :translations {:en "Under the tax laws of the IBO’s Home Jurisdiction, the Partnership set up in the form of a Luxembourg special limited partnership (SCSp, or société en commandite spéciale) is regarded as an entity which is transparent, \"flow-through\" or disregarded"}
    :info {:en "For purposes of this question, \"disregarded\" applies where the Partnership’s income or expenses are treated as income or expenses of the IBO under the laws of the IBO’s Home Jurisdiction."}
    :info-position :below-input}

   :kyc-legal-entity/investor-taxed-on-partnership-income
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-na-options
    :translations {:en "Under the tax laws of its Home Jurisdiction, the Investor is subject to tax and its taxable income includes the Partnership's income as it arises to the Partnership, whether or not distributed to the Investor"}}

   :kyc-legal-entity/ibo-taxed-on-partnership-income
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-na-options
    :translations {:en "Under the tax laws of its Home Jurisdiction, the IBO is subject to tax and its taxable income includes the Partnership’s income as it arises to the Partnership, whether or not distributed to the IBO: "}}

   :kyc-legal-entity/investor-jurisdiction-lacks-tax-rules-on-scsp
   {:data/type :data.type/keyword
    :options kyc-options/true-false-options
    :translations {:en "There are no provisions of law in the Investor's Home Jurisdiction that enable the Investor to determine whether the Partnership is a \"taxable person\" or \"tax transparent\" person for the purposes of the Investor's Home Jurisdiction's tax laws (as the Investor's Home Jurisdiction does not impose tax on income or capital)"}}

   :kyc-legal-entity/ibo-jurisdiction-lacks-tax-rules-on-scsp
   {:data/type :data.type/keyword
    :options kyc-options/true-false-options
    :translations {:en "There are no provisions of law in the IBO’s Home Jurisdiction that enable the IBO to determine whether the Partnership is a \"taxable person\" or \"tax transparent\" person for the purposes of the IBO’s Home Jurisdiction’s tax laws (as the IBO’s Home Jurisdiction does not impose tax on income or capital)."}}

   :kyc-legal-entity/investor-transparent-territorial-tax-exempt-tax-relief-multichoice
   {:data/type :data.type/keyword-multi
    :options   [{:key :transparent
                 :ord 1.0
                 :translations {:en "The Investor is ‘transparent’ for tax purposes (i.e., akin to a ‘partnership’/ ‘flow-through’ / ‘disregarded’ entity) for the purposes of the Investor’s Home Jurisdiction’s tax laws)."}}
                {:key :territorial
                 :ord 2.0
                 :translations {:en "The Investor is subject to tax but its taxable income does not include income received from the Partnership, whether or not distributed to the Investor, due to a territorial tax regime in the Investor’s Home Jurisdiction."}}
                {:key :tax-exempt
                 :ord 3.0
                 :translations {:en "The Investor is exempt from tax in its Home Jurisdiction (excluding a transparent or disregarded person)."}}
                {:key :tax-relief
                 :ord 4.0
                 :translations {:en "The Investor is subject to tax but its taxable income does not include income received from the Partnership, whether or not distributed to the Investor, due to a tax relief granted in the Investor’s Home Jurisdiction."}}]
    :translations {:en "Please identify which of the following statements below apply to the Investor"}}

   :kyc-legal-entity/ibo-transparent-territorial-tax-exempt-tax-relief-multichoice
   {:data/type :data.type/keyword-multi
    :options   [{:key :transparent
                 :ord 1.0
                 :translations {:en "The IBO is ‘transparent’ for tax purposes (i.e., akin to a ‘partnership’ / ‘flow-through’ / ‘disregarded’ entity) for the purposes of the IBO’s Home Jurisdiction’s tax laws)."}}
                {:key :territorial
                 :ord 2.0
                 :translations {:en "The IBO is subject to tax but its taxable income does not include income received from the Partnership, whether or not distributed to the IBO, due to a territorial tax regime in the IBO’s Home Jurisdiction."}}
                {:key :tax-exempt
                 :ord 3.0
                 :translations {:en "The IBO is exempt from tax in its Home Jurisdiction (excluding a transparent or disregarded person)."}}
                {:key :tax-relief
                 :ord 4.0
                 :translations {:en "The IBO is subject to tax but its taxable income does not include income received from the Partnership, whether or not distributed to the IBO, due to a tax relief granted in the IBO’s Home Jurisdiction."}}]
    :translations {:en "Please identify which of the following statements below apply to the Investor"}}

   :kyc-legal-entity/identification-for-all-legal-representatives
   {:data/type :data.type/file
    :translations {:en "Identification for all legal representatives"}
    :info {:en "<p>A copy of a valid identification document issued by a public authority for all legal representatives of the company</p>"}}

   :kyc-legal-entity/confirmation-place
   {:data/type    :data.type/text-simple
    :translations {:en "Place"
                   :fi "Paikka"}}

   :kyc-legal-entity/confirmation-date
   {:data/type         :data.type/date
    :translations      {:en "Date"
                        :fi "Aika"}
    :conditional-field (fn [_answers _kyc-process]
                         false)
    :field/default-authorization {:access/write :role/system}}

   :kyc-legal-entity/confirmation-name
   {:data/type    :data.type/text-simple
    :translations {:en "Name"
                   :fi "Nimi"}}

   :kyc-legal-entity/number-of-signers
   {:data/type    :data.type/keyword
    :options      [{:key          :one
                    :translations {:en "1"
                                   :fi "1"}
                    :ord          1.0}
                   {:key          :two
                    :translations {:en "2"
                                   :fi "2"}
                    :ord          2.0}]
    :translations {:en "Number of signers"
                   :fi "Allekirjoittajien lukumäärä"}}

   :kyc-legal-entity/signer-name-1
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (or (not (template-has-question? kyc-process :kyc-legal-entity/number-of-signers))
                             (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))
    :translations      {:en "Signer 1 - Full name"
                        :fi "Allekirjoittaja 1 - Nimi"}}

   :kyc-legal-entity/signer-email-1
   {:data/type         :data.type/email
    :conditional-field (fn [answers kyc-process]
                         (or (not (template-has-question? kyc-process :kyc-legal-entity/number-of-signers))
                             (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))
    :mandatory-field?  (fn [_answers kyc-process]
                         (= :kyc-process.status/wip (:kyc-process/status kyc-process)))
    :validation-fn     (fn [{:keys [answers]}]
                         (when-not (common-email/optional-valid-email? (:kyc-legal-entity/signer-email-1 answers))
                           :invalid-email))
    :translations      {:en "Signer 1 - Email"
                        :fi "Allekirjoittaja 1 - Sähköpostiosoite"}}

   :kyc-legal-entity/signer-phone-1
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (or (not (template-has-question? kyc-process :kyc-legal-entity/number-of-signers))
                             (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))
    :translations      {:en "Signer 1 - Phone"
                        :fi "Allekirjoittaja 1 - Puhelin"}}

   :kyc-legal-entity/signer-ssn-1
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (or (not (template-has-question? kyc-process :kyc-legal-entity/number-of-signers))
                             (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))
    :translations      {:en "Signer 1 - Social security number"
                        :fi "Allekirjoittaja 1 - Henkilötunnus"}}

   :kyc-legal-entity/type-of-identification-attachment-1
   {:data/type         :data.type/keyword
    :options           kyc-options/signer-identification-options
    :conditional-field (fn [answers kyc-process]
                         (or (not (template-has-question? kyc-process :kyc-legal-entity/number-of-signers))
                             (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))
    :translations      {:en "Signer 1 - Identification type"
                        :fi "Allekirjoittaja 1 - Tunnistamisasiakirjan tyyppi"}}

   :kyc-legal-entity/identification-attachment-1
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (not= :identification.type/provided-in-previous-questions
                               (:kyc-legal-entity/type-of-identification-attachment-1
                                answers)))
    :mandatory-field?  (fn [answers _kyc-process]
                         (not= :identification.type/provided-in-previous-questions
                               (:kyc-legal-entity/type-of-identification-attachment-1
                                answers)))
    :translations
    {:en "Signer 1 - Upload certified copy of a valid identification"
     :fi "Allekirjoittaja 1 - Liitä oikeaksi todistettu kopio voimassaolevasta asiakirjasta"}}

   :kyc-legal-entity/identification-national-id-card-front-side-file-1
   {:data/type :data.type/file
    :translations {:en "Signer 1 - Copy of national ID card (front side)"
                   :fi "Allekirjoittaja 1 - Henkilökortin kopio (etuosa)"}}

   :kyc-legal-entity/identification-national-id-card-back-side-file-1
   {:data/type :data.type/file
    :translations {:en "Signer 1 - Copy of national ID card (back side)"
                   :fi "Allekirjoittaja 1 - Henkilökortin kopio (takaosa)"}}

   :kyc-legal-entity/identification-passport-photo-page-file-1
   {:data/type :data.type/file
    :translations {:en "Signer 1 - Copy of passport (photo page)"
                   :fi "Allekirjoittaja 1 - Passikopio (kuvasivu)"}}

   :kyc-legal-entity/identification-attachment-not-certified-1
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (not= :identification.type/provided-in-previous-questions
                               (:kyc-legal-entity/type-of-identification-attachment-1
                                answers)))
    :mandatory-field?  (fn [answers _kyc-process]
                         (not= :identification.type/provided-in-previous-questions
                               (:kyc-legal-entity/type-of-identification-attachment-1
                                answers)))
    :translations
    {:en "Signer 1 - Upload copy of a valid identification"
     :fi "Allekirjoittaja 1 - Liitä kopio voimassaolevasta asiakirjasta"}}

   :kyc-legal-entity/signer-name-2
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-signers answers)} [:two]))
    :translations      {:en "Signer 2 - Full name"
                        :fi "Allekirjoittaja 2 - Nimi"}}

   :kyc-legal-entity/signer-email-2
   {:data/type         :data.type/email
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-signers answers)} [:two]))
    :mandatory-field?  (fn [_answers kyc-process]
                         (= :kyc-process.status/wip (:kyc-process/status kyc-process)))
    :validation-fn     (fn [{:keys [answers]}]
                         (when-not (common-email/optional-valid-email? (:kyc-legal-entity/signer-email-2 answers))
                           :invalid-email))
    :translations      {:en "Signer 2 - Email"
                        :fi "Allekirjoittaja 2 - Sähköpostiosoite"}}

   :kyc-legal-entity/type-of-identification-attachment-2
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers _kyc-process]
                         (some #{(:kyc-legal-entity/number-of-signers answers)} [:two]))
    :options           kyc-options/signer-identification-options
    :translations      {:en "Signer 2 - Identification type"
                        :fi "Allekirjoittaja 2 - Tunnistamisasiakirjan tyyppi"}}

   :kyc-legal-entity/identification-attachment-2
   {:data/type         :data.type/file
    :conditional-field (fn [answers _kyc-process]
                         (and (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])
                              (not= :identification.type/provided-in-previous-questions
                                    (:kyc-legal-entity/type-of-identification-attachment-2
                                     answers))))
    :mandatory-field?  (fn [answers _kyc-process]
                         (and (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])
                              (not= :identification.type/provided-in-previous-questions
                                    (:kyc-legal-entity/type-of-identification-attachment-2
                                     answers))))
    :translations
    {:en "Signer 2 - Upload certified copy of a valid identification"
     :fi "Allekirjoittaja 2 - Liitä oikeaksi todistettu kopio voimassaolevasta asiakirjasta"}}

   :kyc-legal-entity/identification-national-id-card-front-side-file-2
   {:data/type :data.type/file
    :translations {:en "Signer 2 - Copy of national ID card (front side)"
                   :fi "Allekirjoittaja 2 - Henkilökortin kopio (etuosa)"}}

   :kyc-legal-entity/identification-national-id-card-back-side-file-2
   {:data/type :data.type/file
    :translations {:en "Signer 2 - Copy of national ID card (back side)"
                   :fi "Allekirjoittaja 2 - Henkilökortin kopio (takaosa)"}}

   :kyc-legal-entity/identification-passport-photo-page-file-2
   {:data/type :data.type/file
    :translations {:en "Signer 2 - Copy of passport (photo page)"
                   :fi "Allekirjoittaja 2 - Passikopio (kuvasivu)"}}

   :kyc-legal-entity/signature-info-1
   {:data/type       :data.type/info
    :optional-field? true
    :translations
    {:en
     (str
      "On behalf of the investor, I hereby confirm that the information I have declared "
      "in this form and in its appendices are true and correct. "
      "I undertake to inform any changes to the declared information within 30 days of the change.")
     :fi
     (str
      "Vakuutan, että tässä lomakkeessa ja sen liitteissä olevat tiedot ovat oikeita, "
      "ja että sijoittaja sitoutuu ilmoittamaan näiden tietojen muutoksista 30 päivän kuluessa muutoksesta.")}}

   :kyc-legal-entity/signature-info-2
   {:data/type       :data.type/info
    :optional-field? true
    :translations
    {:en (str "By signing the Client Information Form I understand that the collected personal "
              "data may be shared and disclosed within the company to customer service, "
              "other customer relationship management, risk management and marketing purposes. "
              "The company has a right to disclose  personal data to external service providers, "
              "local tax and financial supervisory authorities in tax reporting "
              "and supervisory purposes.")
     :fi (str "Allekirjoittamalla tämän lomakkeen ymmärrän, että kerättyä henkilötietoa "
              "voidaan siirtää ja luovuttaa yrityksen sisällä asiakaspalveluun "
              "ja muuhun asiakassuhteen hoitamiseen, riskienhallintaan ja markkinointiin. "
              "Yrityksellä voi myös olla oikeus luovuttaa tietoja ulkopuolisille "
              "palveluntarjoajille sekä Verohallinnolle että Finanssivalvonnalle "
              "valvonta- ja veroraportointitarkoituksiin.")}}

   :kyc-legal-entity/signature-place
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (or (some #{(:kyc-process/template kyc-process)}
                                        [:kyc-template/innovestor-cofund-angel-entity
                                         :kyc-template/innovestor-cofund-angel-target-company])
                                  (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two]))))
    :style             "line"
    :translations      {:en "Place"
                        :fi "Paikka"}}

   :kyc-legal-entity/signature-date
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (or (some #{(:kyc-process/template kyc-process)}
                                        [:kyc-template/innovestor-cofund-angel-entity
                                         :kyc-template/innovestor-cofund-angel-target-company])
                                  (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two]))))
    :style             "line"
    :translations      {:en "Date"
                        :fi "Aika"}}
   :kyc-legal-entity/signature-name
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (or (some #{(:kyc-process/template kyc-process)}
                                        [:kyc-template/innovestor-cofund-angel-entity
                                         :kyc-template/innovestor-cofund-angel-target-company])
                                  (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two]))))
    :style             "line"
    :translations      {:en "Name"
                        :fi "Nimi"}}

   :kyc-legal-entity/signature-signature
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (or (some #{(:kyc-process/template kyc-process)}
                                        [:kyc-template/innovestor-cofund-angel-entity
                                         :kyc-template/innovestor-cofund-angel-target-company])
                                  (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two]))))
    :style             "line"
    :translations      {:en "Signature"
                        :fi "Allekirjoitus"}}

   :kyc-legal-entity/signature-place-2
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])))
    :style             "line"
    :translations      {:en "Place"
                        :fi "Paikka"}}

   :kyc-legal-entity/signature-date-2
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])))
    :style             "line"
    :translations      {:en "Date"
                        :fi "Aika"}}
   :kyc-legal-entity/signature-name-2
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])))
    :style             "line"
    :translations      {:en "Name"
                        :fi "Nimi"}}

   :kyc-legal-entity/signature-signature-2
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers kyc-process]
                         (and (some #{(:kyc-process/status kyc-process)}
                                    [:kyc-process.status/signatures
                                     :kyc-process.status/waiting-for-e-sign])
                              (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])))
    :style             "line"
    :translations      {:en "Signature"
                        :fi "Allekirjoitus"}}

   :kyc-legal-entity/sole-shareholder
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "I hereby confirm that I own at least 75% of the investor’s voting shares and that I have sole control over the investor
                        (i.e., I am able to make the investment decision in question on my own)."
                   :fi "Vahvistan täten, että omistan vähintään 75 % sijoittajan äänivaltiaisista osakkeista ja minulla on yksin määräysvalta
                        sijoittajassa (eli pystyn yksin tekemään kyseisen sijoituspäätöksen)."}}
   :kyc-legal-entity/confirm-marketing-consent
   {:data/type    :data.type/confirm
    :translations {:en "I give my consent for the provided contact information to be used for direct marketing by electronic means."
                   :fi "Annan suostumukseni ilmoittamieni yhteystietojen käyttämiseen sähköisessä suoramarkkinoinnissa."}}

   :kyc-legal-entity/primary-contact-person
   {:data/type    :data.type/text-multiline
    :translations {:en "Primary contact person (Provide name, phone number, and email for general correspondence.)"
                   :fi "Ensisijainen yhteyshenkilö"}
    :info         {:en "Provide name, phone number, and email for general correspondence."
                   :fi "Anna nimi, puhelinnumero ja sähköpostiosoite yleistä kirjeenvaihtoa varten."}}

   :kyc-legal-entity/primary-contact-person-full-name
   {:data/type :data.type/text-simple
    :translations {:en "Full name of primary contact person"}}

   :kyc-legal-entity/primary-contact-person-email
   {:data/type :data.type/text-simple
    :translations {:en "Email of primary contact person"}}

   :kyc-legal-entity/primary-contact-person-phone
   {:data/type :data.type/text-simple
    :translations {:en "Phone number of primary contact person"}}

   :kyc-legal-entity/contact-for-drawdown-differs-from-primary-contact-person
   {:data/type :data.type/keyword
    :options kyc-options/yes-no-options
    :translations {:en "Does the contact for drawdown notices differ from primary contact person?"}}

   :kyc-legal-entity/contact-for-drawdown-notices-full-name
   {:data/type :data.type/text-simple
    :translations {:en "Full name of contact for drawdown notices"}}

   :kyc-legal-entity/contact-for-drawdown-notices-email
   {:data/type :data.type/text-simple
    :translations {:en "Email of contact for drawdown notices"}}

   :kyc-legal-entity/contact-for-drawdown-notices-phone
   {:data/type :data.type/text-simple
    :translations {:en "Phone number of contact for drawdown notices"}}

   :kyc-legal-entity/contact-for-drawdown-notices
   {:data/type       :data.type/text-multiline
    :optional-field? true
    :translations    {:en "Contact for Drawdown Notices"
                      :fi "Yhteyshenkilö nostopyyntöjä varten"}
    :info            {:en "Provide details for Drawdown Notices (if different from A1) or attach contact matrix."
                      :fi "Anna yhteystiedot nostopyyntöjä varten (jos eroaa A1:stä) tai liitä yhteysmatriisi."}}

   :kyc-legal-entity/professional-investors-client-type
   {:data/type         :data.type/keyword
    :editor/type       :editor.type/radio
    :translations     {:en "The Investor possess the experience, knowledge and expertise to make its own investment decisions and properly assess the risks involved and is one of the following types of client:"}
    :options          [{:key :authorised-or-regulated-entity
                        :ord 1
                        :translations {:en "An entity which is required to be authorised or regulated to operate in the financial markets (including entities authorised by a European Union member state (a “Member State”) under a directive, entities authorised or regulated by a Member State without reference to a directive, and entities authorised or regulated by a non-Member State)"}}
                       {:key :large-undertaking
                        :ord 2
                        :translations {:en "A large undertaking meeting two of the following size requirements on a company basis: (i) Balance sheet total equal to or exceeding : €20,000,000, (ii) Net turnover total equal to or exceeding : €40,000,000, (iii) Own funds total equal to or exceeding : €2,000,000."}}
                       {:key :government-public-body
                        :ord 3
                        :translations {:en "A national or regional government, public body that manages public debt, central and regional banks, international and supranational institutions (such as the World Bank, the International Monetary Fund, the European Central Bank, the European Investment Bank) and any other similar organisation."}}
                       {:key :any-other-institutional-investor
                        :ord 4
                        :translations {:en "Any other institutional investor whose main activity is to invest in financial instruments specified in Section C of Annex I to MiFID II, including entities dedicated to the securitisation of assets or other financial transactions."}}]}

   :kyc-legal-entity/authorised-or-regulated-entity-type
   {:data/type :data.type/keyword
    :translations {:en "Specify investor type"}
    :options [{:key :credit-institution
               :ord 1
               :translations {:en "A credit institution"}}
              {:key :investment-firm
               :ord 2
               :translations {:en "An investment firm"}}
              {:key :other-authorised-or-regulated-financial-institution
               :ord 3
               :translations {:en "Any other authorised or regulated financial institution"}}
              {:key :insurance-company
               :ord 4
               :translations {:en "An insurance company"}}
              {:key :collective-investment-scheme-or-management-company-of-such-scheme
               :ord 5
               :translations {:en "A collective investment scheme or a management company of such a scheme"}}
              {:key :pension-fund-or-management-company-of-such-pension-fund
               :ord 6
               :translations {:en "A pension fund or a management company of such a pension fund"}}
              {:key :commondity-or-commodity-derivatives-dealer
               :ord 7
               :translations {:en "A commodity or commodity derivatives dealer"}}
              {:key :local-firm-within-the-meaning-of-directive-2013-36-eu
               :ord 8
               :translations {:en "A local firm within the meaning of Directive 2013/36/EU"}}
              {:key :any-other-institutional-investor
               :ord 9
               :translations {:en "Any other institutional investor"}}]}

   :kyc-legal-entity/authorised-and-supervised-by
   {:data/type :data.type/text-simple
    :translations {:en "The investor is authorised and supervised by the following authority:"}}

   :kyc-legal-entity/any-other-institutional-investor-justification
   {:data/type :data.type/text-multiline
    :translations {:en "Please describe the operations / give reasons for why the Investor considers to be such investor:"}}

   ;; Subscription Agreement Appendix B - Regulatory Questions
   :kyc-legal-entity/is-authorised-regulated-entity
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the Investor an entity which is required to be authorised or regulated to operate in the financial markets?"
                   :fi "Onko sijoittaja yhteisö, joka on velvollinen hankkimaan toimiluvan tai joka on säännelty toimiakseen rahoitusmarkkinoilla?"}}

   :kyc-legal-entity/large-undertaking
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the Investor a large undertaking meeting at least two of the following financial thresholds (based on the latest financial statements)?"
                   :fi "Onko sijoittaja suuri yritys, joka täyttää vähintään kaksi seuraavista taloudellisista raja-arvoista (viimeisimpien tilinpäätösten perusteella)?"}}

   :kyc-legal-entity/balance-sheet-threshold
   {:data/type         :data.type/keyword
    :options           kyc-options/yes-no-options
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/large-undertaking answers)))
    :translations      {:en "Balance sheet total equal to or exceeding €20,000,000"
                        :fi "Taseen loppusumma vähintään 20 000 000 euroa"}}

   :kyc-legal-entity/net-turnover-threshold
   {:data/type         :data.type/keyword
    :options           kyc-options/yes-no-options
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/large-undertaking answers)))
    :translations      {:en "Net turnover total equal to or exceeding €40,000,000"
                        :fi "Liikevaihto vähintään 40 000 000 euroa"}}

   :kyc-legal-entity/own-funds-threshold
   {:data/type         :data.type/keyword
    :options           kyc-options/yes-no-options
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/large-undertaking answers)))
    :translations      {:en "Own funds total equal to or exceeding €2,000,000"
                        :fi "Oma pääoma vähintään 2 000 000 euroa"}}

   :kyc-legal-entity/government-public-body
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the Investor a national or regional government, public body managing public debt, central/regional bank, or international institution (e.g., IMF, World Bank)?"
                   :fi "Onko sijoittaja kansallinen tai alueellinen hallitus, julkista velkaa hallinnoiva elin, keskus-/alueellinen pankki tai kansainvälinen instituutio (esim. IMF, Maailmanpankki)?"}}

   :kyc-legal-entity/institutional-investor-main-activity
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is the Investor another institutional investor whose main activity is to invest in financial instruments (Section C of Annex I to MiFID II)?"
                   :fi "Onko sijoittaja muu institutionaalinen sijoittaja, jonka päätoiminta on sijoittaminen rahoitusvälineisiin (MiFID II:n liitteen I kohta C)?"}}

   :kyc-legal-entity/institutional-investor-description
   {:data/type         :data.type/text-multiline
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/institutional-investor-main-activity answers)))
    :translations      {:en "Please describe the operations / give reasons why the Investor considers itself such an investor"
                        :fi "Kuvaile toimintaa / anna syyt, miksi sijoittaja pitää itseään sellaisena sijoittajana"}}

   ;; Subscription Agreement Appendix B - Investment Experience
   :kyc-legal-entity/portfolio-over-500k
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Are you holding a financial instruments portfolio with a value greater than EUR 500,000?"
                   :fi "Onko sinulla yli 500 000 euron sijoitussalkku?"}}

   :kyc-legal-entity/financial-institution-experience
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Are you or have you been a director or an officer of a credit establishment or an investment firm or any other financial service firm (asset manager, etc.)?"}}

   :kyc-legal-entity/financial-institution-experience-description
   {:data/type         :data.type/text-multiline
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/financial-institution-experience answers)))
    :translations      {:en "Describe the nature and duration of your experience"}}

   :kyc-legal-entity/financial-services-employment
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Have you worked for an investment firm or a credit institution or any other financial services firm for at least one year in a position requiring knowledge in investments in undertakings for collective investment regulated or not?"}}

   :kyc-legal-entity/financial-services-employment-description
   {:data/type         :data.type/text-multiline
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/financial-services-employment answers)))
    :translations      {:en "Please describe the nature and duration of your previous experience in a position as a professional in finance that required knowledge regarding investments in regulated or unregulated undertakings for collective investment:"}}

   :kyc-legal-entity/frequent-investment-activity
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Have you carried out an average of at least 10 major investment transactions per quarter in significant size in financial instruments over the previous four quarters?"}}

   :kyc-legal-entity/portfolio-statement
   {:data/type         :data.type/file
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/frequent-investment-activity answers)))
    :translations      {:en "Please provide a copy of your portfolio statement presenting the operations made over the past four months."}}

   :kyc-legal-entity/collective-investment-experience
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Have you previously invested or advised an investment in regulated or unregulated undertakings for collective investment and/or private equity transactions?"}}

   :kyc-legal-entity/collective-investment-experience-description
   {:data/type         :data.type/text-multiline
    :translations      {:en "Please briefly summarize the types of transactions you have carried out"}}


   :kyc-legal-entity/lifeline-crs-classification-info
   {:data/type :data.type/info
    :optional-field? true
    :translations {:en "Provide your CRS classification. Note that CRS classification does not necessarily coincide with your classification for FATCA purposes"}}

   :kyc-legal-entity/lifeline-crs-entity-is-financial-institution-combo
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/combo-checkbox-selection
    :selection-mode    :radio
    :translations      {:en "If the entity is a Financial Institution, please tick this box and specify the type of Financial Institution in (a), (b), or (c) below (where the entity is resident in a Participating Jurisdiction, use the terms as defined under the CRS regime in that Jurisdiction. Where the entity is resident in a Non-Participating Jurisdiction, definitions under the Luxembourg CRS regime must be used.):"}
    :children          [{:key :reporting-financial-institution-under-crs
                         :label {:en "(a) Reporting Financial Institution under CRS. (Please note this classification only applies to a Financial Institution in a CRS Participating Jurisdiction. If the entity is a Financial Institution in a Non-Participating Jurisdiction under CRS, proceed to (c))."}}
                        {:key :non-reporting-financial-institution-under-crs
                         :label {:en "(b) Non-Reporting Financial Institution under CRS.  (Please note this classification only applies to a Financial Institution in a CRS Participating Jurisdiction.  If the entity is a Financial Institution in a Non-Participating Jurisdiction under CRS, proceed to (c))."}}
                        {:key :investment-entity-resident-in-a-non-participating-jurisdiction-under-crs
                         :label {:en "(c) Investment Entity resident in a Non-Participating Jurisdiction under CRS and managed by another Financial Institution"}}]}

   :kyc-legal-entity/lifeline-crs-type-of-non-reporting-financial-institution
   {:data/type :data.type/keyword
    :translations {:en "Specify the type of Non-Reporting Financial Institution"}
    :options [{:key :governmental-entity
               :translations {:en "Governmental Entity"}
               :ord 1.0}
              {:key :international-organization
               :translations {:en "International Organization"}
               :ord 2.0}
              {:key :central-bank
               :translations {:en "Central Bank"}
               :ord 3.0}
              {:key :broad-participation-retirement-fund
               :translations {:en "Broad Participation Retirement Fund"}
               :ord 4.0}
              {:key :narrow-participation-retirement-fund
               :translations {:en "Narrow Participation Retirement Fund"}
               :ord 5.0}
              {:key :pension-fund-of-governmental-entity-intl-org-or-central-bank
               :translations {:en "Pension Fund of a Governmental Entity, International Organization, or Central Bank"}
               :ord 6.0}
              {:key :exempt-collective-investment-vehicle
               :translations {:en "Exempt Collective Investment Vehicle"}
               :ord 7.0}
              {:key :trust-with-trustee-reporting-crs-reportable-accounts
               :translations {:en "Trust whose trustee reports all required information with respect to all CRS Reportable Accounts"}
               :ord 8.0}
              {:key :qualified-credit-card-issuer
               :translations {:en "Qualified Credit Card Issuer"}
               :ord 9.0}
              {:key :other-entity-defined-under-domestic-law-as-low-risk-being-used-to-evade-tax
               :translations {:en "Other Entity defined under the domestic law as low risk of being used to evade tax. "}
               :ord 10.0}]}

   :kyc-legal-entity/lifeline-crs-low-risk-type-of-non-reporting-financial-institution-provided-in-domestic-law
   {:data/type :data.type/text-simple
    :translations {:en "Specify the type provided in the domestic law"}}

   :kyc-legal-entity/lifeline-crs-entity-is-active-non-financial-entity-combo
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/combo-checkbox-selection
    :selection-mode    :radio
    :translations      {:en "If the entity is an Active Non-Financial Entity (“NFE”) please tick this box and specify the type of Active NFE below:"}
    :children          [{:key :regularly-traded-corporation
                         :label {:en "Corporation that is regularly traded or a related entity of a regularly traded corporation."}}
                        {:key :governmental-entity-intl-org-central-bank
                         :label {:en "Governmental Entity, International Organization, a Central Bank, or an Entity wholly owned by one or more of the foregoing."}}
                        {:key :other-active-non-financial-entity
                         :label {:en "Other Active Non-Financial Entity."}}]}



   :kyc-legal-entity/lifeline-crs-active-nfe-name-of-regularly-traded-corporation
   (let [condition-fn (fn [answers]
                        (-> answers
                            :kyc-legal-entity/lifeline-crs-entity-is-active-non-financial-entity-combo
                            :checked-children
                            set
                            (contains? :regularly-traded-corporation)))]
     {:data/type :data.type/text-simple
      :translations {:en "If you are a related entity of a regularly traded corporation, provide the name of the regularly traded corporation"}
      :conditional-field condition-fn
      :optional-field? true})



   :kyc-legal-entity/lifeline-crs-entity-is-passive-non-financial-entity?
   {:data/type :data.type/boolean
    :editor/type :editor.type/checkbox
    :translations {:en "If the entity is a Passive Non-Financial Entity please tick this box"}}


   ;; E1 - Entity with assets > US$5M combo checkbox
   :kyc-legal-entity/entity-with-assets-excess-of-5m-purpose-not-investing-in-the-partnership-combo
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/combo-checkbox-selection
    :selection-mode    :multi-select
    :children          [{:key :corporation
                         :label {:en "a corporation"}}
                        {:key :partnership
                         :label {:en "a partnership"}}
                        {:key :limited-liability-company
                         :label {:en "a limited liability company"}}
                        {:key :business-trust
                         :label {:en "a business trust"}}
                        {:key :tax-exempt-organization
                         :label {:en "a tax-exempt organisation described in section 501(c)(3) of the Code."}}]
    :translations      {:en "It is an entity with total assets in excess of US$5,000,000 which was not formed for the purpose of investing in the Partnership and which is one of the following:"}}

   :kyc-legal-entity/legal-entity-is-an-investment-adviser-combo
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/combo-checkbox-selection
    :selection-mode    :multi-select
    :children          [{:key :registered-section-203
                         :label {:en "registered pursuant to Section 203 of the Advisers Act or pursuant to the laws of a state;"}}
                        {:key :relying-on-exemption
                         :label {:en "relying on the exemption from registering with the SEC under Section 203(l) (the “venture capital fund adviser” exemption) of, or Rule 203(m)-1 (the “private fund adviser” exemption) under, the Advisers Act."}}]
    :translations      {:en "It is an investment adviser"}}

   ;; E4 - Employee benefit plan combo checkbox
   :kyc-legal-entity/employee-benefit-plan-within-meaning-of-title-1-of-erisa-combo
   {:data/type         :data.type/text-edn
    :edn-data/type     :edn-data.type/combo-checkbox-selection
    :selection-mode    :multi-select  ; or :radio for single selection
    :children          [{:key :assets-over-5m
                         :label {:en "Plan with total assets in excess of US$5,000,000"
                                 :fi "Järjestely, jonka kokonaisvarat ylittävät 5 000 000 USD"}}
                        {:key :plan-fiduciary-decision
                         :label {:en "Investment decisions are made by a plan fiduciary that is a bank, savings and loan association, insurance company, or registered investment adviser"
                                 :fi "Sijoituspäätökset tekee järjestelyn luottamushenkilö, joka on pankki, säästö- ja lainayhdistys, vakuutusyhtiö tai rekisteröity sijoitusneuvoja"}}]
    :translations      {:en "It is an employee benefit plan within the meaning of Title I of ERISA which satisfies at least one of the following conditions:"}}

   :kyc-legal-entity/self-directed-plan
   {:data/type :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "it is a self-directed plan (i.e., a tax qualified defined contribution plan in which a participant may exercise control over the investment of assets credited to his or her account) and the decision to invest is made by those participants investing, and each such participant qualifies as an accredited investor."}}

   ;; E3 - Personal trust > US$5M
   :kyc-legal-entity/personal-trust-5m
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is a personal (non-business) trust, other than an employee benefit trust, with total assets in excess of US$5,000,000 which was not formed for the purpose of investing in the Partnership and whose decision to invest in the Partnership has been directed by a person who has such knowledge and experience in financial and business matters that he or she is capable of evaluating the merits and risks of the investment."}}

   ;; E5 - State employee benefit plan
   :kyc-legal-entity/state-employee-benefit-plan
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is an employee benefit plan established and maintained by a state, its political subdivisions or any agency or instrumentality of a state or its political subdivisions, for the benefit of its employees, which has total assets in excess of US$5,000,000."}}

   :kyc-legal-entity/licensed-supervised-entity
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is licensed, or subject to supervision, by federal or state examining authorities, such as a “bank,” “savings and loan association,” “insurance company,” or “small business investment company” (as such terms are used and defined in 17 CFR §230.501(a)) or is an account for which a bank or savings and loan association is subscribing in a fiduciary capacity."}}

   :kyc-legal-entity/sec-registered-entity
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is registered with the SEC as a broker or dealer or an investment company; or has elected to be treated or qualifies as a “business development company” (within the meaning of section 2(a)(48) of the Investment Company Act or section 202(a)(22) of the Advisers Act."}}

   :kyc-legal-entity/other-entity-5m
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is an entity of a type not described above, with total assets in excess of US$5,000,000, not formed for the specific purpose of making an investment in the Partnership, owning not less than US$5,000,000 in “investments,” as defined in Rule 2a51-1(b) under the Investment Company Act."}}

   ;; E9 - Family office
   :kyc-legal-entity/family-office
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is a “family office,” as defined in Rule 202(a)(11)(G)-1 under the Advisers Act, (i) with assets under management in excess of US$5,000,000, (ii) that is not formed for the specific purpose of making an investment in the Partnership, and (iii) whose prospective investment is directed by a person who has such knowledge and experience in financial and business matters that such family office is capable of evaluating the merits and risks of the prospective investment."}}

   ;; E10 - Family client
   :kyc-legal-entity/family-client
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is a “family client,” as defined in Rule 202(a)(11)(G)-1 under the Advisers Act, “family office,” as each term is defined in Rule 202(a)(11)(G)-1 under the Advisers Act, whose prospective investment in the Partnership is directed by a person at such family office who has such knowledge and experience in financial and business matters that such family office is capable of evaluating the merits and risks of the prospective investment."}}

   ;; E11 - Entity with accredited equity owners
   :kyc-legal-entity/entity-accredited-owners
   {:data/type    :data.type/boolean
    :editor/type  :editor.type/checkbox
    :translations {:en "It is an entity in which all of the equity owners are persons described above. Note: If it chooses this option, a separate response to this paragraph 2 must be submitted on behalf of each of its beneficial owners."}}

   ;; E12-E20 Investment Company Act questions
   :kyc-legal-entity/is-investment-company-registered
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-options
    :translations {:en "The Investor hereby represents, warrants and confirms that it is: (1) an \"investment company,\" as defined in Section 3(a) of the Investment Company Act, registered or required to be registered under the Investment Company Act; or (2) a \"business development company,\" as defined in Section 2(a)(48) of the Investment Company Act."
                   :fi "Sijoittaja täten edustaa, takaa ja vahvistaa, että se on: (1) \"sijoitusyhtiö\" sijoitusyhtiölain pykälän 3(a) mukaisesti, rekisteröity tai velvollinen rekisteröitymään sijoitusyhtiölain mukaisesti; tai (2) \"liiketoiminnan kehittämisyhtiö\" sijoitusyhtiölain pykälän 2(a)(48) mukaisesti."}}

   :kyc-legal-entity/would-be-investment-company
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-options
    :translations {:en "The Investor would be an \"investment company\" as defined in Section 3(a) of the Investment Company Act if it were not exempt due to Section 3(c)(1) or 3(c)(7)."
                   :fi "Sijoittaja olisi \"sijoitusyhtiö\" sijoitusyhtiölain pykälän 3(a) mukaisesti, jos se ei olisi vapautettu pykälien 3(c)(1) tai 3(c)(7) mukaisesti."}}

   :kyc-legal-entity/commitment-less-than-10-percent
   {:data/type         :data.type/keyword
    :options           kyc-options/true-false-options
    :conditional-field (fn [answers]
                         (or (= :true (:kyc-legal-entity/is-investment-company-registered answers))
                             (= :true (:kyc-legal-entity/would-be-investment-company answers))))
    :translations      {:en "The Investor's Commitment to the Partnership is less than 10% of the Partnership's committed capital committed by all of its Investors."}}

   :kyc-legal-entity/not-formed-for-partnership-investment
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-options
    :translations {:en "The Investor was not formed or reformed (as interpreted under the Investment Company Act) for the purpose of acquiring the Interests of the Partnership."
                   :fi "Sijoittajaa ei ole perustettu tai uudelleenmuodostettu (sijoitusyhtiölain mukaisesti tulkittuna) hankkimaan kumppanuuden etuja."}}

   :kyc-legal-entity/commitment-less-than-40-percent-assets
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-options
    :translations {:en "The Investor's Commitment to the Partnership is less than forty percent (40%) of the Investor's assets (including committed capital)."
                   :fi "Sijoittajan sitoumus kumppanuuteen on alle neljäkymmentä prosenttia (40 %) sijoittajan varoista (mukaan lukien sitoutunut pääoma)."}}

   :kyc-legal-entity/has-made-other-investments
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-options
    :translations {:en "The Investor has made investments prior to the date hereof or intends to make investments in the near future and each of its beneficial owner of interests has shared and will share in the same proportion in each such investment (e.g., none of its beneficial owners may vary its interests in different investments made by or on behalf of it)."}}

   :kyc-legal-entity/governing-docs-require-proportional-participation
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-options
    :translations {:en "The Investor's governing documents require that each of its beneficial owners including, but not limited to, shareholders, partners and beneficiaries, participate through his, her or its interest in it in all of it's investments and that the profits and losses from each such investment are shared among such beneficial owners in the same proportions as all of its other investments. No such beneficial owner may vary his, her or its share of the profits and losses or the amount of his, her or its contribution for any investment made by it."}}

   :kyc-legal-entity/managed-as-collective-vehicle
   {:data/type    :data.type/keyword
    :options      kyc-options/true-false-options
    :translations {:en "The Investor is not managed as a device for facilitating individual investment decisions of its beneficial owners, but rather is managed as a collective investment vehicle."
                   :fi "Sijoittajaa ei hallita välineenä, joka helpottaa sen tosiasiallisten edunsaajien yksittäisiä sijoituspäätöksiä, vaan sitä hallitaan yhteissijoitusvälineenä."}}

   :kyc-legal-entity/number-of-beneficial-owners
   {:data/type         :data.type/number
    :editor/type       :editor.type/integer
    :conditional-field (fn [answers]
                         (some #{:false}
                               [(:kyc-legal-entity/commitment-less-than-40-percent-assets answers)
                                (:kyc-legal-entity/not-formed-for-partnership-investment answers)
                                (:kyc-legal-entity/has-made-other-investments answers)
                                (:kyc-legal-entity/governing-docs-require-proportional-participation answers)
                                (:kyc-legal-entity/managed-as-collective-vehicle answers)]))
    :translations      {:en "The number of direct or indirect beneficial owners of the undersigned's securities is:"}}

   :kyc-legal-entity/qualified-purchaser-qualifications
   {:data/type :data.type/keyword-multi
    :options      [{:key :natural-person-5m-or-more-in-investments
                    :ord 1
                    :translations {:en "(a) a natural person who owns US$5,000,000 or more in \"investments.\" In making this determination, subtract the amount of any outstanding indebtedness incurred to make the \"investments\""}}
                   {:key :company-defined-in-section-2-a-8-of-the-investment-act
                    :ord 2
                    :translations {:en "(b) a company as defined in Section 2(a)(8) of the Investment Company Act6 (i) that owns US$5,000,000 or more in “investments” and that is owned directly or indirectly by or for two or more natural persons who are related as siblings or spouse (including former spouses), or direct lineal descendants by birth or adoption, spouses of such persons (including former spouses), the estates of such persons, or foundations, charitable organizations, or trusts established by or for the benefit of such persons (a “Family Company”) and (ii) not formed for the specific purpose of investing in the Partnership;"}}
                   {:key :qualified-purchaser-trust
                    :ord 3
                    :translations {:en "(c) a trust that (i) was not formed for the specific purpose of investing in the Partnership and (ii) is not a Family Company as to which the trustees (or other persons authorised to make decisions for the trust) and each of whose settlors or other persons (e.g., grantors) who has contributed assets to the trust, is a qualified purchaser as described in (i), (ii) or (iv);"}}
                   {:key :discretionary-investor
                    :ord 4
                    :translations {:en "(d) any person, (i) acting for its own account or the accounts of other qualified purchasers, who in the aggregate owns and invests on a discretionary basis, not less than US$25,000,000 in “investments” and (ii) was not formed for the specific purpose of investing in the Partnership. In making this determination, subtract the amount of any outstanding indebtedness incurred to make the “investments”;"}}
                   {:key :qualified-purchaser-qib
                    :ord 5
                    :translations {:en "(e) a “qualified institutional buyer” within the meaning of Rule 144A of the Securities Act (“Rule 144A”), acting for its own account, the account of another qualified institutional buyer, or the account of a qualified purchaser provided: (A) a dealer described in paragraph (a)(1)(ii) of Rule 144A owns and invests on a discretionary basis at least $25,000,000 in securities of issuers that are not affiliated persons of the dealer; and (B) a plan referred to in paragraph (a)(1)(D) or (a)(1)(E) of Rule 144A or a trust fund referred to in paragraph (a)(1)(F) of Rule 144A that holds the assets of such a plan, will not be deemed to be acting for its own account if investment decisions with respect to the plan are made by the beneficiaries of the plan (as might be the case, for example, in a 401(k) plan);"}}
                   {:key :company-all-qualified
                    :ord 6
                    :translations {:en "(f) a company (other than a trust) all of whose securities are beneficially owned by persons or entities described in one or more of categories (a) through (e) of this section 4. Note: If the Investor ticks box (f) only, a separate response to this Section 4 must be submitted on behalf of each of the Investor's beneficial owners."}}]
    :translations {:en "Tick the box next to the category or categories which describes it:"}
    :info         {:en "The Investor is a “qualified purchaser” as that term is defined in Section 2(a)(51) of the Investment Company Act and has indicated below the basis for its status as such (for this purpose, the term “investments” has the meaning set forth in Rule 2a51-1 under the Investment Company Act, and the amount of its “investments” has been determined in the manner required by such Rule 2a51-1 (summarized below)):"}
    :info-position :above-input}

   :kyc-legal-entity/qualified-purchaser-pre-1996-company
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "If it is a company formed on or before April 30, 1996 that relies on the exceptions in Section 3(c)(1) or 3(c)(7) of the Investment Company Act, it hereby represents and warrants that all consents required under the Investment Company Act to its treatment as a qualified purchaser have been obtained."
                   :fi "Jos se on yhtiö, joka on perustettu 30. huhtikuuta 1996 tai sitä ennen ja joka luottaa sijoitusyhtiölain pykälien 3(c)(1) tai 3(c)(7) poikkeuksiin, se täten edustaa ja takaa, että kaikki sijoitusyhtiölain mukaiset suostumukset sen kohteluun pätevänä ostajana on saatu."}}

   ;; E28 - Bank Holding Company Act
   :kyc-legal-entity/subject-to-bank-holding-company-act
   {:data/type    :data.type/keyword
    :options      kyc-options/yes-no-options
    :translations {:en "Is it subject to the Bank Holding Company Act of 1956, as amended?"
                   :fi "Onko se vuoden 1956 pankkiholdingyhtiölain, sellaisena kuin se on muutettuna, alainen?"}}

   ;; =================
   ;; TAX FORM B PART 2 
   ;; =================

   ;; 2.2.3 - Exemption indication for non-specified US persons
   :kyc-legal-entity/us-person-exemption-indication
   {:data/type         :data.type/text-simple
    :translations      {:en "Indicate exemption (if not Specified U.S. Person)"}}

   :kyc-legal-entity/us-tin
   {:data/type :data.type/text-simple
    :translations {:en "U.S. TIN"}}

   ;; 3.1.1 - Registration in progress checkbox  
   :kyc-legal-entity/giin-registration-in-progress
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (= :yes (:kyc-legal-entity/financial-institution? answers)))
    :translations      {:en "Tax Form B Part 2 - 3.1.1 - Registration in progress"
                        :fi "Veroilmoitus B Osa 2 - 3.1.1 - Rekisteröinti käynnissä"}}

   ;; 3.2 - Classification of FI without GIIN
   :kyc-legal-entity/fi-without-giin-classification
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (and (= :yes (:kyc-legal-entity/financial-institution? answers))
                              (= :no (:kyc-legal-entity/have-GIIN? answers))))
    :options           [{:key          :sponsored
                         :translations {:en "Sponsored"
                                        :fi "Sponsoroitu"}
                         :ord          1.0}
                        {:key          :trustee-documented-trust
                         :translations {:en "Trustee Documented Trust"
                                        :fi "Trustee Documented Trust"}
                         :ord          2.0}
                        {:key          :certified-deemed-compliant
                         :translations {:en "Certified Deemed Compliant or Non-Reporting"}
                         :ord          3.0}
                        {:key          :non-participating
                         :translations {:en "Non-participating"}
                         :ord          4.0}]
    :translations      {:en "Tax Form B Part 2 - 3.2 - Classification of FI without GIIN"
                        :fi "Veroilmoitus B Osa 2 - 3.2 - FI:n luokitus ilman GIIN:iä"}}

   ;; 3.2.1 - Sponsoring Entity's name
   :kyc-legal-entity/sponsoring-entity-name
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :sponsored (:kyc-legal-entity/fi-without-giin-classification answers)))
    :mandatory-field?  (fn [answers]
                         (= :sponsored (:kyc-legal-entity/fi-without-giin-classification answers)))
    :translations      {:en "Tax Form B Part 2 - 3.2.1 - Sponsoring Entity's name"
                        :fi "Veroilmoitus B Osa 2 - 3.2.1 - Sponsoroivan yksikön nimi"}}

   ;; 3.2.2 - Sponsoring Entity's GIIN
   :kyc-legal-entity/sponsoring-entity-giin
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :sponsored (:kyc-legal-entity/fi-without-giin-classification answers)))
    :mandatory-field?  (fn [answers]
                         (= :sponsored (:kyc-legal-entity/fi-without-giin-classification answers)))
    :translations      {:en "Tax Form B Part 2 - 3.2.2 - Sponsoring Entity's GIIN"
                        :fi "Veroilmoitus B Osa 2 - 3.2.2 - Sponsoroivan yksikön GIIN"}}

   ;; 3.2.3 - Sponsored Entity's GIIN
   :kyc-legal-entity/sponsored-entity-giin
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :sponsored (:kyc-legal-entity/fi-without-giin-classification answers)))
    :optional-field?   true
    :translations      {:en "Tax Form B Part 2 - 3.2.3 - Sponsored Entity's GIIN (optional)"
                        :fi "Veroilmoitus B Osa 2 - 3.2.3 - Sponsoroidun yksikön GIIN (valinnainen)"}}

   ;; 3.2.4 - Trustee's name
   :kyc-legal-entity/trustee-name
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :trustee-documented-trust (:kyc-legal-entity/fi-without-giin-classification answers)))
    :mandatory-field?  (fn [answers]
                         (= :trustee-documented-trust (:kyc-legal-entity/fi-without-giin-classification answers)))
    :translations      {:en "Tax Form B Part 2 - 3.2.4 - Trustee's name"
                        :fi "Veroilmoitus B Osa 2 - 3.2.4 - Trustee:n nimi"}}

   ;; 3.2.5 - Trustee's GIIN
   :kyc-legal-entity/trustee-giin
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :trustee-documented-trust (:kyc-legal-entity/fi-without-giin-classification answers)))
    :mandatory-field?  (fn [answers]
                         (= :trustee-documented-trust (:kyc-legal-entity/fi-without-giin-classification answers)))
    :translations      {:en "Tax Form B Part 2 - 3.2.5 - Trustee's GIIN"
                        :fi "Veroilmoitus B Osa 2 - 3.2.5 - Trustee:n GIIN"}}

   ;; 3.2.6 - Exemption indication for Certified Deemed Compliant
   :kyc-legal-entity/certified-deemed-compliant-exemption
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :certified-deemed-compliant (:kyc-legal-entity/fi-without-giin-classification answers)))
    :mandatory-field?  (fn [answers]
                         (= :certified-deemed-compliant (:kyc-legal-entity/fi-without-giin-classification answers)))
    :translations      {:en "Tax Form B Part 2 - 3.2.6 - Indicate exemption (if Certified Deemed Compliant)"
                        :fi "Veroilmoitus B Osa 2 - 3.2.6 - Ilmoita vapautus (jos todistettu noudattava)"}}

   ;; 3.3 - Not a foreign financial institution
   :kyc-legal-entity/not-foreign-financial-institution
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/financial-institution? answers)))
    :translations      {:en "Tax Form B Part 2 - 3.3 - Not a foreign financial institution"
                        :fi "Veroilmoitus B Osa 2 - 3.3 - Ei ulkomainen finanssilaitos"}}

   ;; 3.4 - Exempt Beneficial Owner
   :kyc-legal-entity/exempt-beneficial-owner
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/financial-institution? answers)))
    :translations      {:en "Tax Form B Part 2 - 3.4 - Exempt Beneficial Owner"
                        :fi "Veroilmoitus B Osa 2 - 3.4 - Vapautettu tosiasiallinen edunsaaja"}}

   ;; 3.5 - Active Non-Financial Foreign Entity
   :kyc-legal-entity/active-non-financial-foreign-entity
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/financial-institution? answers)))
    :translations      {:en "Tax Form B Part 2 - 3.5 - Active Non-Financial Foreign Entity"
                        :fi "Veroilmoitus B Osa 2 - 3.5 - Aktiivinen ei-finanssinen ulkomainen yksikkö"}}

   ;; 3.5.1 - Qualifying criteria for Active NFFE
   :kyc-legal-entity/active-nffe-qualifying-criteria
   {:data/type         :data.type/text-simple
    :translations      {:en "Indicate qualifying criteria for Active Non-Financial Foreign Entity"}}

   ;; 3.6 - Direct Reporting NFFE
   :kyc-legal-entity/direct-reporting-nffe
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/financial-institution? answers)))
    :translations      {:en "Tax Form B Part 2 - 3.6 - Direct Reporting NFFE"
                        :fi "Veroilmoitus B Osa 2 - 3.6 - Suoraan raportoiva NFFE"}}

   :kyc-legal-entity/direct-reporting-nffe-giin
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (:kyc-legal-entity/direct-reporting-nffe answers))
    :translations      {:en "Direct Reporting NFFE's GIIN"}}


   :kyc-legal-entity/sponsored-direct-reporting-nffe-sponsoring-entity-name
   {:data/type :data.type/text-simple
    :translations {:en "Sponsoring Entity's Name"}}

   :kyc-legal-entity/sponsored-direct-reporting-nffe-sponsoring-entity-giin
   {:data/type :data.type/text-simple
    :translations {:en "Sponsoring Entity's GIIN"}}

   :kyc-legal-entity/sponsored-direct-reporting-nffe-sponsored-entity-giin
   {:data/type :data.type/text-simple
    :translations {:en "Sponsored Entity's GIIN"}}

   :kyc-legal-entity/sponsored-direct-reporting-nffe
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/financial-institution? answers)))
    :translations      {:en "Tax Form B Part 2 - 3.7 - Sponsored Direct Reporting NFFE"
                        :fi "Veroilmoitus B Osa 2 - 3.7 - Sponsoroitu suoraan raportoiva NFFE"}}

   ;; 3.7.1 - Sponsored Direct Reporting NFFE - Sponsoring Entity's name
   :kyc-legal-entity/sponsored-nffe-sponsoring-entity-name
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (:kyc-legal-entity/sponsored-direct-reporting-nffe answers))
    :translations      {:en "Tax Form B Part 2 - 3.7.1 - Sponsoring Entity's name"
                        :fi "Veroilmoitus B Osa 2 - 3.7.1 - Sponsoroivan yksikön nimi"}}

   ;; 3.7.2 - Sponsored Direct Reporting NFFE - Sponsoring Entity's GIIN
   :kyc-legal-entity/sponsored-nffe-sponsoring-entity-giin
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (:kyc-legal-entity/sponsored-direct-reporting-nffe answers))
    :translations      {:en "Tax Form B Part 2 - 3.7.2 - Sponsoring Entity's GIIN"
                        :fi "Veroilmoitus B Osa 2 - 3.7.2 - Sponsoroivan yksikön GIIN"}}

   ;; 3.7.3 - Sponsored Direct Reporting NFFE - Sponsored Entity's GIIN
   :kyc-legal-entity/sponsored-nffe-sponsored-entity-giin
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (:kyc-legal-entity/sponsored-direct-reporting-nffe answers))
    :translations      {:en "Tax Form B Part 2 - 3.7.3 - Sponsored Entity's GIIN"
                        :fi "Veroilmoitus B Osa 2 - 3.7.3 - Sponsoroidun yksikön GIIN"}}

   ;; 3.8 - Passive Non-Financial Foreign Entity
   :kyc-legal-entity/passive-non-financial-foreign-entity
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (= :no (:kyc-legal-entity/financial-institution? answers)))
    :translations      {:en "Tax Form B Part 2 - 3.8 - Passive Non-Financial Foreign Entity"
                        :fi "Veroilmoitus B Osa 2 - 3.8 - Passiivinen ei-finanssinen ulkomainen yksikkö"}}

   :kyc-legal-entity/list-substantial-us-owners-or-controlling-persons-info
   {:data/type :data.type/info
    :optional-field? true
    :translations {:en "Please complete either Substantial U.S. Owners OR Controlling Persons below:"}}

   :kyc-legal-entity/substantial-us-owners-edn
   {:data/type :data.type/text-edn
    :edn-data/type :edn-data.type/substantial-us-owner-list
    :data/template nil ;; MUST BE OVERRIDDEN by schema to avoid circular references 
    :translations {:en "Substantial U.S. Owners"}
    :info         {:en "If the Entity has chosen to use the definition of ‘Substantial U.S. Owner’ from the U.S. Treasury Regulations in lieu of the definition of ‘Controlling Person’ as permitted under Article 4(7) of the Agreement between the Government of Luxembourg and the Government of the United States of America to Improve International Tax Compliance and to Implement FATCA, please complete the table below providing details of any Substantial U.S. Owners."}}

   :kyc-legal-entity/controlling-persons-edn
   {:data/type :data.type/text-edn
    :edn-data/type :edn-data.type/controlling-person-list
    :data/template nil ;; MUST BE OVERRIDDEN by schema to avoid circular references
    :translations {:en "Controlling Persons"}
    :info         {:en "Alternatively, if you use the Controlling Person definition (aligned with FATF and CRS), please complete the following."}}

   :kyc-legal-entity/crs-reporting-financial-institution
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         ;; Show when CRS classification indicates FI
                         (some #{(:kyc-legal-entity/crs-classification answers)}
                               [:crs-institution/custodial
                                :crs-institution/investment-entity-in-non-participating-jurisdiction]))
    :mandatory-field?  (fn [answers]
                         (some #{(:kyc-legal-entity/crs-classification answers)}
                               [:crs-institution/custodial
                                :crs-institution/investment-entity-in-non-participating-jurisdiction]))
    :translations      {:en "Tax Form B Part 3 - 5.1.a - Reporting Financial Institution"
                        :fi "Veroilmoitus B Osa 3 - 5.1.a - Raportoiva finanssilaitos"}}

   ;; 5.1.b - Non-Reporting FI (specify type)
   :kyc-legal-entity/crs-non-reporting-fi-type
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (some #{(:kyc-legal-entity/crs-classification answers)}
                               [:crs-institution/custodial
                                :crs-institution/investment-entity-in-non-participating-jurisdiction]))
    :mandatory-field?  (fn [answers]
                         (some #{(:kyc-legal-entity/crs-classification answers)}
                               [:crs-institution/custodial
                                :crs-institution/investment-entity-in-non-participating-jurisdiction]))
    :options           [{:key          :government
                         :translations {:en "Government"
                                        :fi "Valtio"}
                         :ord          1.0}
                        {:key          :international-organization
                         :translations {:en "International Organization"
                                        :fi "Kansainvälinen järjestö"}
                         :ord          2.0}
                        {:key          :central-bank
                         :translations {:en "Central Bank"
                                        :fi "Keskuspankki"}
                         :ord          3.0}
                        {:key          :other
                         :translations {:en "Other"
                                        :fi "Muu"}
                         :ord          4.0}]
    :translations      {:en "Tax Form B Part 3 - 5.1.b - Non-Reporting FI (specify type)"
                        :fi "Veroilmoitus B Osa 3 - 5.1.b - Ei-raportoiva FI (määritä tyyppi)"}}

   ;; 5.1.b.1 - Domestic legal type (if 'Other')
   :kyc-legal-entity/crs-domestic-legal-type
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :other (:kyc-legal-entity/crs-non-reporting-fi-type answers)))
    :mandatory-field?  (fn [answers]
                         (= :other (:kyc-legal-entity/crs-non-reporting-fi-type answers)))
    :translations      {:en "Tax Form B Part 3 - 5.1.b.1 - Domestic legal type (if 'Other')"
                        :fi "Veroilmoitus B Osa 3 - 5.1.b.1 - Kotimainen oikeudellinen tyyppi (jos 'Muu')"}}

   ;; 5.1.c - Investment Entity in NPJ, managed by FI
   :kyc-legal-entity/crs-investment-entity-npj-managed-by-fi
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (some #{(:kyc-legal-entity/crs-classification answers)}
                               [:crs-institution/custodial
                                :crs-institution/investment-entity-in-non-participating-jurisdiction]))
    :mandatory-field?  (fn [answers]
                         (some #{(:kyc-legal-entity/crs-classification answers)}
                               [:crs-institution/custodial
                                :crs-institution/investment-entity-in-non-participating-jurisdiction]))
    :translations      {:en "Tax Form B Part 3 - 5.1.c - Investment Entity in NPJ, managed by FI"
                        :fi "Veroilmoitus B Osa 3 - 5.1.c - Sijoitusyksikkö NPJ:ssä, FI:n hallinnoima"}}

   ;; 5.1.c.1 - Full name(s) of Controlling Persons
   :kyc-legal-entity/crs-controlling-persons-names
   {:data/type         :data.type/text-multiline
    :conditional-field (fn [answers]
                         (:kyc-legal-entity/crs-investment-entity-npj-managed-by-fi answers))
    :mandatory-field?  (fn [answers]
                         (:kyc-legal-entity/crs-investment-entity-npj-managed-by-fi answers))
    :translations      {:en "Tax Form B Part 3 - 5.1.c.1 - Full name(s) of Controlling Persons"
                        :fi "Veroilmoitus B Osa 3 - 5.1.c.1 - Määräysvaltaisten henkilöiden koko nimet"}}

   ;; 5.2 - Is the entity an Active NFE?
   :kyc-legal-entity/crs-active-nfe
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         ;; Show when NOT a financial institution under CRS
                         (not (some #{(:kyc-legal-entity/crs-classification answers)}
                                    [:crs-institution/custodial
                                     :crs-institution/investment-entity-in-non-participating-jurisdiction])))
    :mandatory-field?  (fn [answers]
                         (not (some #{(:kyc-legal-entity/crs-classification answers)}
                                    [:crs-institution/custodial
                                     :crs-institution/investment-entity-in-non-participating-jurisdiction])))
    :translations      {:en "Tax Form B Part 3 - 5.2 - Is the entity an Active NFE?"
                        :fi "Veroilmoitus B Osa 3 - 5.2 - Onko yksikkö aktiivinen NFE?"}}

   ;; 5.2.1 - Specify the type of Active NFE
   :kyc-legal-entity/crs-active-nfe-type
   {:data/type         :data.type/keyword
    :conditional-field (fn [answers]
                         (:kyc-legal-entity/crs-active-nfe answers))
    :mandatory-field?  (fn [answers]
                         (:kyc-legal-entity/crs-active-nfe answers))
    :options           [{:key          :traded
                         :translations {:en "Traded"
                                        :fi "Pörssinoteerattu"}
                         :ord          1.0}
                        {:key          :related-entity
                         :translations {:en "Related Entity"
                                        :fi "Lähiyhtiö"}
                         :ord          2.0}
                        {:key          :government
                         :translations {:en "Government"
                                        :fi "Valtio"}
                         :ord          3.0}
                        {:key          :other
                         :translations {:en "Other"
                                        :fi "Muu"}
                         :ord          4.0}]
    :translations      {:en "Tax Form B Part 3 - 5.2.1 - Specify the type of Active NFE"
                        :fi "Veroilmoitus B Osa 3 - 5.2.1 - Määritä aktiivisen NFE:n tyyppi"}}

   ;; 5.2.1.a - Name of stock exchange
   :kyc-legal-entity/crs-stock-exchange-name
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :traded (:kyc-legal-entity/crs-active-nfe-type answers)))
    :mandatory-field?  (fn [answers]
                         (= :traded (:kyc-legal-entity/crs-active-nfe-type answers)))
    :translations      {:en "Tax Form B Part 3 - 5.2.1.a - Name of stock exchange"
                        :fi "Veroilmoitus B Osa 3 - 5.2.1.a - Pörssin nimi"}}

   ;; 5.2.1.b - Name of related regularly traded corporation
   :kyc-legal-entity/crs-related-traded-corporation
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :related-entity (:kyc-legal-entity/crs-active-nfe-type answers)))
    :mandatory-field?  (fn [answers]
                         (= :related-entity (:kyc-legal-entity/crs-active-nfe-type answers)))
    :translations      {:en "Tax Form B Part 3 - 5.2.1.b - Name of related regularly traded corporation"
                        :fi "Veroilmoitus B Osa 3 - 5.2.1.b - Lähiyhtiön säännöllisesti kaupankäynnin kohteena olevan yhtiön nimi"}}

   ;; 5.2.1.c - Qualifying criteria for Other Active NFE
   :kyc-legal-entity/crs-other-active-nfe-criteria
   {:data/type         :data.type/text-simple
    :conditional-field (fn [answers]
                         (= :other (:kyc-legal-entity/crs-active-nfe-type answers)))
    :mandatory-field?  (fn [answers]
                         (= :other (:kyc-legal-entity/crs-active-nfe-type answers)))
    :translations      {:en "Tax Form B Part 3 - 5.2.1.c - Indicate qualifying criteria (Other Active NFE)"
                        :fi "Veroilmoitus B Osa 3 - 5.2.1.c - Ilmoita pätevyyskriteerit (Muu aktiivinen NFE)"}}

   ;; 5.3 - Is the entity a Passive NFE?
   :kyc-legal-entity/crs-passive-nfe
   {:data/type         :data.type/boolean
    :conditional-field (fn [answers]
                         (and (not (some #{(:kyc-legal-entity/crs-classification answers)}
                                         [:crs-institution/custodial
                                          :crs-institution/investment-entity-in-non-participating-jurisdiction]))
                              (not (:kyc-legal-entity/crs-active-nfe answers))))
    :mandatory-field?  (fn [answers]
                         (and (not (some #{(:kyc-legal-entity/crs-classification answers)}
                                         [:crs-institution/custodial
                                          :crs-institution/investment-entity-in-non-participating-jurisdiction]))
                              (not (:kyc-legal-entity/crs-active-nfe answers))))
    :translations      {:en "Tax Form B Part 3 - 5.3 - Is the entity a Passive NFE?"
                        :fi "Veroilmoitus B Osa 3 - 5.3 - Onko yksikkö passiivinen NFE?"}}

   :kyc-legal-entity/lifeline-fatca-is-financial-institution
   {:data/type    :data.type/keyword
    :translations {:en "Is the entity a Financial Institution (FATCA definition)?"}
    :options      kyc-options/yes-no-options}

   :kyc-legal-entity/lifeline-does-the-entity-have-fatca-giin
   {:data/type :data.type/keyword
    :translations {:en "Does the entity have its own FATCA GIIN (or registration is in progress)?"}
    :options [{:key :yes
               :ord 1.0
               :translations {:en "Yes"}}
              {:key :registration-in-progress
               :ord 2.0
               :translations {:en "Registration in progress"}}
              {:key :no
               :ord 3.0
               :translations {:en "No"}}]}

   :kyc-legal-entity/lifeline-fatca-registered-ffi-category
   {:data/type    :data.type/keyword
    :editor/type  :editor.type/radio
    :translations {:en "If the entity is a Registered Foreign Financial Institution, select one category"}
    :options      [{:key :reporting-model-1-ffi
                    :ord 1.0
                    :translations {:en "Reporting Model 1 FFI"}}
                   {:key :registered-deemed-compliant-foreign-financial-institution
                    :ord 2.0
                    :translations {:en "Registered Deemed Compliant Foreign Financial Institution (other than a reporting Model 1 FFI, sponsored FFI, or non-reporting IGA FFI)"}}
                   {:key :reporting-model-2-ffi
                    :ord 3.0
                    :translations {:en "Reporting Model 2 FFI"}}
                   {:key :participating-foreign-financial-institution
                    :ord 4.0
                    :translations {:en "Participating Foreign Financial Institution"}}]}

   :kyc-legal-entity/lifeline-fatca-giin
   {:data/type :data.type/text-simple
    :translations {:en "Global Intermediary Identification Number (GIIN)"}}

   :kyc-legal-entity/lifeline-fatca-giin-expected-issuance-date
   {:data/type    :data.type/date
    :translations {:en "Registration in progress - expected GIIN issuance date"}}

   :kyc-legal-entity/lifeline-fatca-fi-no-giin-category
   {:data/type    :data.type/keyword
    :editor/type  :editor.type/radio
    :translations {:en "If the entity is a Financial Institution but is unable to provide a GIIN or has a Sponsored Entity GIIN, select one category"}
    :options      [{:key :sponsored-financial-institution
                    :ord 1.0
                    :translations {:en "The Entity is a Sponsored Financial Institution (sponsored by another entity that has registered as a Sponsoring Entity)."}}
                   {:key :trustee-documented-trust
                    :ord 2.0
                    :translations {:en "The Entity is a Trustee Documented Trust."}}
                   {:key :certified-deemed-compliant-or-otherwise-non-reporting-ffi
                    :ord 3.0
                    :translations {:en "The Entity is a Certified Deemed Compliant, or otherwise Non-Reporting, Foreign Financial Institution (including a Foreign Financial Institution deemed compliant under Annex II of an IGA, except for a Trustee Documented Trust or Sponsored Financial Institution)."}}
                   {:key :non-participating-ffi
                    :ord 4.0
                    :translations {:en "The Entity is a Non-Participating Foreign Financial Institution."}}]}

   :kyc-legal-entity/lifeline-fatca-no-giin-sponsored-financial-institution-type
   {:data/type    :data.type/keyword
    :editor/type  :editor.type/radio
    :translations {:en "Sponsored Financial Institution - select one"}
    :options      [{:key :no-us-reportable-accounts-and-sponsored-fi
                    :ord 1.0
                    :translations {:en "has no US reportable accounts, is a Sponsored FI in a Model 1 IGA jurisdiction and therefore not required to obtain a Sponsored Entity GIIN. Please provide the Sponsoring Entity's name and GIIN."}}
                   {:key :sponsor-has-obtained-giin-on-its-behalf
                    :ord 2.0
                    :translations {:en "its Sponsor has obtained a Sponsored Entity GIIN on its behalf. Please provide the Sponsoring Entity's name and GIIN, and Sponsored Entity's GIIN."}}]}

   :kyc-legal-entity/lifeline-fatca-giin-sponsoring-entity-name
   {:data/type :data.type/text-simple
    :translations {:en "Sponsoring Entity's Name"}}

   :kyc-legal-entity/lifeline-fatca-giin-sponsoring-entity-giin
   {:data/type :data.type/text-simple
    :translations {:en "Sponsoring Entity's GIIN"}}

   :kyc-legal-entity/lifeline-fatca-giin-sponsored-entity-giin
   {:data/type    :data.type/text-simple
    :translations {:en "Sponsored Entity's GIIN"}}

   :kyc-legal-entity/lifeline-fatca-trustee-name
   {:data/type :data.type/text-simple
    :translations {:en "Trustee's Name"}}

   :kyc-legal-entity/lifeline-fatca-trustee-giin
   {:data/type :data.type/text-simple
    :translations {:en "Trustee's GIIN"}}

   :kyc-legal-entity/lifeline-fatca-certified-deemed-compliant-or-otherwise-non-reporting-exemption
   {:data/type     :data.type/text-simple
    :translations  {:en "Indicate exemption"}
    :info          {:en "Identify the applicable deemed-compliant or non-reporting status"}
    :info-position :below-input}

   :kyc-legal-entity/lifeline-fatca-nffe-fatca-status
   {:data/type     :data.type/keyword
    :editor/type   :editor.type/radio
    :translations  {:en "If the entity is not a Foreign Financial Institution, confirm the entity's FATCA status"}
    :options       [{:key :exempt-beneficial-owner
                     :ord 1.0
                     :translations {:en "The Entity is an Exempt Beneficial Owner."}}
                    {:key :active-nffe
                     :ord 2.0
                     :translations {:en "The Entity is an Active Non-Financial Foreign Entity. Indicate qualifying criteria."}}
                    {:key :direct-reporting-nffe
                     :ord 3.0
                     :translations {:en "The Entity is a Direct Reporting NFFE. Please provide the Entity's GIIN."}}
                    {:key :sponsored-direct-reporting-nffe
                     :ord 4.0
                     :translations {:en "The Entity is a Sponsored Direct Reporting NFFE. Please provide the Sponsoring Entity's name and GIIN."}}
                    {:key :passive-nffe
                     :ord 5.0
                     :translations {:en "The Entity is a Passive Non-Financial Foreign Entity."}}]}

   :kyc-legal-entity/lifeline-fatca-nffe-exempt-beneficial-owner-indicate-status
   {:data/type    :data.type/text-simple
    :translations {:en "Exempt Beneficial Owner - Indicate status"}}

   :kyc-legal-entity/lifeline-fatca-nffe-active-nffe-indicate-qualifying-criteria
   {:data/type    :data.type/text-simple
    :translations {:en "Active Non-Financial Foreign Entity - Indicate qualifying criteria (see FATCA Definitions)"}}

   :kyc-legal-entity/lifeline-fatca-nffe-direct-reporting-nffe-giin
   {:data/type    :data.type/text-simple
    :translations {:en "Direct Reporting NFFE's GIIN"}}

   :kyc-legal-entity/lifeline-fatca-nffe-sponsored-direct-reporting-nffe-sponsoring-entity-name
   {:data/type :data.type/text-simple
    :translations {:en "Sponsoring Entity's Name"}}

   :kyc-legal-entity/lifeline-fatca-nffe-sponsored-direct-reporting-nffe-sponsoring-entity-giin
   {:data/type :data.type/text-simple
    :translations {:en "Sponsoring Entity's GIIN"}}

   :kyc-legal-entity/lifeline-fatca-nffe-sponsored-direct-reporting-nffe-sponsored-entity-giin
   {:data/type :data.type/text-simple
    :translations {:en "Sponsored Entity's GIIN"}}

   :kyc-legal-entity/lifeline-crs-is-financial-institution
   {:data/type    :data.type/keyword
    :translations {:en "Is the entity a Financial Institution (CRS definition)?"}
    :options      kyc-options/yes-no-options}

   :kyc-legal-entity/lifeline-crs-fi-classification
   {:data/type    :data.type/keyword
    :editor/type  :editor.type/radio
    :translations {:en "Provide your CRS classification by checking the corresponding option"}
    :options      [{:key :reporting-financial-institution-under-crs
                    :ord 1.0
                    :translations {:en "Reporting Financial Institution under CRS. (This classification only applies to a Financial Institution in a CRS Participating Jurisdiction. If the entity is a Financial Institution in a Non-Participating Jurisdiction under CRS, select \"Investment Entity resident in a Non-Participating Jurisdiction under CRS and managed by another Financial Institution\".)"}}
                   {:key :non-reporting-financial-institution-under-crs
                    :ord 2.0
                    :translations {:en "Non-Reporting Financial Institution under CRS. (This classification only applies to a Financial Institution in a CRS Participating Jurisdiction.) Specify the type of Non-Reporting Financial Institution below."}}
                   {:key :investment-entity-resident-in-a-non-participating-jurisdiction-under-crs-and-managed-by-other-fi
                    :ord 3.0
                    :translations {:en "Investment Entity resident in a Non-Participating Jurisdiction under CRS and managed by another Financial Institution."}}]}

   :kyc-legal-entity/lifeline-crs-nfe-classification
   {:data/type    :data.type/keyword
    :editor/type  :editor.type/radio
    :translations {:en "Non-Financial Entity classification under CRS"}
    :options      [{:key :active-nfe
                    :ord 1.0
                    :translations {:en "Active Non-Financial Entity (NFE)"}}
                   {:key :passive-nfe
                    :ord 2.0
                    :translations {:en "Passive Non-Financial Entity (NFE)"}}]}

   :kyc-legal-entity/lifeline-crs-active-nfe-type
   {:data/type    :data.type/keyword
    :editor/type  :editor.type/radio
    :translations {:en "Specify the type of Active Non-Financial Entity"}
    :options      [{:key :regularly-traded-corporation-or-related
                    :ord 1.0
                    :translations {:en "Corporation that is regularly traded or a related entity of a regularly traded corporation."}}
                   {:key :governmental-entity-intl-org-central-bank
                    :ord 2.0
                    :translations {:en "Governmental Entity, International Organization, a Central Bank, or an Entity wholly owned by one or more of the foregoing."}}
                   {:key :other-active-non-financial-entity
                    :ord 3.0
                    :translations {:en "Other Active Non-Financial Entity."}}]}

   :kyc-legal-entity/lifeline-crs-active-nfe-is-itself-regularly-traded
   {:data/type    :data.type/keyword
    :translations {:en "Is the entity itself regularly traded on a stock exchange?"}
    :options      kyc-options/yes-no-options}

   :kyc-legal-entity/lifeline-crs-active-nfe-name-of-stock-exchange-where-traded
   {:data/type :data.type/text-simple
    :translations {:en "Name of the stock exchange where traded"}}

   :kyc-legal-entity/lifeline-crs-active-nfe-name-of-related-traded-corporation
   {:data/type :data.type/text-simple
    :translations {:en "Name of the regularly traded corporation"}}

   :kyc-legal-entity/lifeline-crs-other-active-nfe-qualifying-criteria
   {:data/type :data.type/text-simple
    :translations {:en "Indicate qualifying criteria"}
    :info {:en "Describe how the entity meets the Active NFE criteria (see CRS definitions)"}
    :info-position :below-input}})
